import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Star, LogOut, Send, RefreshCw, Trash2, Bell, Mail, Zap, Globe, Loader2, MapPin, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import FeedDebugPanel from "../components/admin/FeedDebugPanel";
import AutoSendStatus from "../components/admin/AutoSendStatus";
import SenseCheckStatus from "../components/admin/SenseCheckStatus";
import BulkVacancyUpload from "../components/admin/BulkVacancyUpload";
import CandidateResponsesPanel from "../components/CandidateResponsesPanel";

const STAGE_LABELS = {
  interest_registered:                "Interest registered",
  email_verified:                     "Email verified",
  email_verified_pending_password:    "Pending password setup",
  stage_one_passed:                   "Stage 1 — Basic Eligibility",
  stage_one_hold:                     "Stage 1 — Hold",
  stage_two_behavioural_profiling:    "Stage 2 — Operational Behaviour Assessment",
  stage_three_aptitude_tests:         "Stage 3 — Aptitude Tests",
  stage_four_cv_submission:           "Stage 4 — CV Submission",
  stage_five_simulated_interview:     "Stage 5 — Simulated Interview",
  stage_six_recorded_interview:       "Stage 6 — Recorded Interview",
};

const STAGE_OPTIONS = [
  { value: "interest_registered",             label: "Interest registered" },
  { value: "email_verified",                  label: "Email verified" },
  { value: "email_verified_pending_password", label: "Pending password setup" },
  { value: "stage_one_hold",                  label: "Stage 1 — Hold" },
  { value: "stage_one_passed",                label: "Stage 1 — Basic Eligibility ✓" },
  { value: "stage_two_behavioural_profiling", label: "Stage 2 — Operational Behaviour Assessment" },
  { value: "stage_three_aptitude_tests",      label: "Stage 3 — Aptitude Tests" },
  { value: "stage_four_cv_submission",        label: "Stage 4 — CV Submission" },
  { value: "stage_five_simulated_interview",  label: "Stage 5 — Simulated Interview" },
  { value: "stage_six_recorded_interview",    label: "Stage 6 — Recorded Interview" },
];
const ADMIN_STATUS_LABELS = {
  new: "New",
  reviewing: "Reviewing",
  on_hold: "On hold",
  approved: "Approved",
  active: "Active",
  shortlist_ready: "Shortlist ready",
  paused: "Paused",
  rejected: "Rejected",
};
const formatStatusLabel = (value) => String(value || "—").replaceAll("_", " ");
const formatStageLabel = (value) => STAGE_LABELS[String(value || "").toLowerCase()] || formatStatusLabel(value);
const formatAdminStatusLabel = (value) => ADMIN_STATUS_LABELS[String(value || "").toLowerCase()] || formatStatusLabel(value);
const stageBadgeClass = (stage) => {
  const key = String(stage || "").toLowerCase();
  if (key.includes("stage_two") || key.includes("stage_three") || key.includes("stage_four") || key.includes("stage_five") || key.includes("stage_six")) {
    return "bg-lime-100 text-lime-900 border-lime-300";
  }
  if (key.includes("hold")) return "bg-amber/20 text-ink border-amber/50";
  return "bg-sky/10 text-ink border-sky/30";
};
const adminBadgeClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "approved" || key === "shortlist_ready") return "bg-lime-100 text-lime-900 border-lime-300";
  if (key === "reviewing" || key === "new") return "bg-sky/10 text-ink border-sky/30";
  if (key === "on_hold" || key === "paused") return "bg-amber/20 text-ink border-amber/50";
  if (key === "rejected") return "bg-red-100 text-red-900 border-red-300";
  return "bg-paper text-ink border-border";
};
const TALENT_ROLE_LABELS = {
  "train-driver": "Train driver",
  conductor: "Conductor",
  "depot-driver": "Depot driver",
  "maintenance-rolling-stock": "Maintenance (Rolling Stock)",
  "maintenance-track-infrastructure": "Maintenance (Track or Infrastructure)",
  administrative: "Administrative",
  graduate: "Graduate",
  signalling: "Signalling",
  other: "Other",
};
const prettyTalentRole = (value) => TALENT_ROLE_LABELS[String(value || "").toLowerCase()] || formatStatusLabel(value);

const isAdzunaUrl = (url) => /adzuna\./i.test(String(url || ""));

const getVacancyLinkQuality = (vacancy) => {
  const src = String(vacancy?.source || "").toLowerCase();
  const isAdzunaSource = src === "adzuna" || src === "adzuna-auto";
  if (!isAdzunaSource) return "native";

  const applyUrl = String(vacancy?.apply_url || "");
  const sourceApplyUrl = String(vacancy?.source_apply_url || "");
  const hasResolved = !!applyUrl && !isAdzunaUrl(applyUrl) && !!sourceApplyUrl && applyUrl !== sourceApplyUrl;
  return hasResolved ? "resolved" : "fallback";
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [vacancies, setVacancies] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [talentMembers, setTalentMembers] = useState([]);
  const [talentDrafts, setTalentDrafts] = useState({});
  const [viewingTalentMember, setViewingTalentMember] = useState(null);
  const [tab, setTab] = useState("vacancies");
  const [vacancyLinkFilter, setVacancyLinkFilter] = useState("all");

  // Newsletter
  const [nlSubject, setNlSubject] = useState("Railway Careers UK — Weekly Briefing");
  const [nlIntro, setNlIntro] = useState("Here are this week's freshest UK railway vacancies.");
  const [nlExtra, setNlExtra] = useState("");
  const [nlPreview, setNlPreview] = useState(null);
  const [nlTest, setNlTest] = useState("");
  const [linkedinPost, setLinkedinPost] = useState(null);
  const [linkedinLoading, setLinkedinLoading] = useState(false);

  // Subscriber notifications
  const [subMsgSubject, setSubMsgSubject] = useState("");
  const [subMsgBody, setSubMsgBody] = useState("");
  const [subMsgFormat, setSubMsgFormat] = useState("text");
  const [subMsgTestEmail, setSubMsgTestEmail] = useState("");
  const [subMsgRecipientMode, setSubMsgRecipientMode] = useState("subscribers_all");
  const [subMsgRecipientEmail, setSubMsgRecipientEmail] = useState("");
  const [subMsgTalentStage, setSubMsgTalentStage] = useState("");
  const [subMsgExternalEmails, setSubMsgExternalEmails] = useState("");
  const [subMsgPreview, setSubMsgPreview] = useState(null);
  const [subMsgPreviewing, setSubMsgPreviewing] = useState(false);
  const [subMsgSending, setSubMsgSending] = useState(false);

  // In-app notifications
  const [inappSubject, setInappSubject] = useState("");
  const [inappBody, setInappBody] = useState("");
  const [inappFormat, setInappFormat] = useState("text");
  const [inappMode, setInappMode] = useState("employer_verified");
  const [inappRecipientEmail, setInappRecipientEmail] = useState("");
  const [inappStage, setInappStage] = useState("");
  const [inappSending, setInappSending] = useState(false);
  const [inappThreads, setInappThreads] = useState([]);
  const [inappArchivedThreads, setInappArchivedThreads] = useState([]);
  const [inappActiveThread, setInappActiveThread] = useState(null);
  const [inappMessages, setInappMessages] = useState([]);
  const [inappReplyBody, setInappReplyBody] = useState("");
  const [inappReplySending, setInappReplySending] = useState(false);
  const [inappSearch, setInappSearch] = useState("");
  const [inappUnreadOnly, setInappUnreadOnly] = useState(false);
  const [inappSelectedIds, setInappSelectedIds] = useState([]);

  // New depot
  const [depot, setDepot] = useState({ name: "", employer_name: "", address: "", postcode: "", lat: "", lng: "", roles: "" });

  // Job feeds (auto-scrape)
  const [feeds, setFeeds] = useState([]);
  const [newFeed, setNewFeed] = useState({ name: "", url: "", employer_hint: "", auto_feature: false, active: true });
  const [feedSyncing, setFeedSyncing] = useState({});

  // External quick-add
  const [ext, setExt] = useState({
    apply_url: "", title: "", employer_name: "", role_category: "train-driver",
    location: "", region: "", salary_min: "", salary_max: "", employment_type: "full-time",
    description: "", expiry_date: "", featured: false,
  });

  // Bulk sync (Adzuna + Sitemap crawler)
  const [adzKw, setAdzKw] = useState("train driver, qualified train driver, trainee train driver, apprentice train driver, conductor, apprentice conductor, trainee conductor, apprentice conductor, trainee train manager, train manager, signaller, railway engineer, depot driver, depot, shunter, shunter driver, fitter, permanent way, p-way, rolling stock, traction, dispatcher, platform staff, platform, booking office, onboard, on board, customer host,");
  const [adzPages, setAdzPages] = useState(3);
  const [adzRunning, setAdzRunning] = useState(false);

  // Geocode backfill (one-shot tool for legacy vacancies missing lat/lng)
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Description cleanup (decode entities + strip tags + drop non-UK)
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [adzResult, setAdzResult] = useState(null);
  const [adzLast, setAdzLast] = useState(null);
  const [smapUrl, setSmapUrl] = useState("");
  const [smapHint, setSmapHint] = useState("");
  const [smapRunning, setSmapRunning] = useState(false);
  const [smapResult, setSmapResult] = useState(null);

  // AI Scout (multi-source discovery + cross-reference)
  const [scoutQuery, setScoutQuery] = useState("train driver rail vacancies uk");
  const [scoutSources, setScoutSources] = useState("");
  const [scoutIncludeKnown, setScoutIncludeKnown] = useState(true);
  const [scoutRunning, setScoutRunning] = useState(false);
  const [scoutResult, setScoutResult] = useState(null);
  const [scoutOnlyNew, setScoutOnlyNew] = useState(true);
  const [scoutMinScore, setScoutMinScore] = useState(55);
  const [scoutVerifyingKey, setScoutVerifyingKey] = useState("");

  const refresh = async () => {
    const [s, v, e, sub, c, d, f, tpm] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/employer/vacancies"),
      api.get("/admin/employers"),
      api.get("/admin/subscribers"),
      api.get("/admin/contact-requests"),
      api.get("/depots"),
      api.get("/admin/job-feeds"),
      api.get("/admin/talent-pool/members"),
    ]);
    setStats(s.data); setVacancies(v.data); setEmployers(e.data);
    setSubscribers(sub.data); setContacts(c.data); setDepots(d.data);
    setFeeds(f.data);
    setTalentMembers(tpm.data || []);
    setTalentDrafts(Object.fromEntries((tpm.data || []).map((m) => [
      m.id,
      {
        talent_pool_admin_status: m.talent_pool_admin_status || "new",
        talent_pool_stage: m.talent_pool_stage || "interest_registered",
        talent_pool_notes: m.talent_pool_notes || "",
      },
    ])));

    const [threadsOpen, threadsArchived] = await Promise.all([
      api.get("/admin/inapp/threads", { params: { archived: false, limit: 400 } }).catch(() => ({ data: [] })),
      api.get("/admin/inapp/threads", { params: { archived: true, limit: 400 } }).catch(() => ({ data: [] })),
    ]);
    setInappThreads(threadsOpen.data || []);
    setInappArchivedThreads(threadsArchived.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const handleLogout = () => { logout(); nav("/"); };

  const runAdzuna = async () => {
    const keywords = adzKw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!keywords.length) { toast.error("Add at least one keyword"); return; }
    setAdzRunning(true); setAdzResult(null);
    try {
      const r = await api.post("/admin/adzuna/sync", {
        keywords, pages_per_keyword: parseInt(adzPages) || 1, results_per_page: 50,
      });
      setAdzResult(r.data);
      toast.success(`Adzuna: ${r.data.inserted} new vacancies (out of ${r.data.matched_toc} matched)`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Adzuna sync failed");
    } finally { setAdzRunning(false); }
  };

  const fetchAdzLast = async () => {
    try {
      const r = await api.get("/admin/adzuna/last-sync");
      setAdzLast(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchAdzLast(); }, []);

  const runSitemap = async () => {
    if (!smapUrl) { toast.error("Paste a careers URL"); return; }
    setSmapRunning(true); setSmapResult(null);
    try {
      const r = await api.post("/admin/sitemap/sync", {
        careers_url: smapUrl, employer_hint: smapHint || null, max_jobs: 80,
      });
      setSmapResult(r.data);
      toast.success(`Sitemap: ${r.data.inserted} new vacancies (${r.data.job_urls} job URLs scanned)`);
      refresh();
    } catch (err) {
      const msg = err.response?.data?.detail || "Sitemap sync failed";
      setSmapResult({ error: msg });
      toast.error(msg);
    } finally { setSmapRunning(false); }
  };

  const runScout = async () => {
    if (!scoutQuery.trim()) { toast.error("Add a scout query"); return; }
    setScoutRunning(true);
    setScoutResult(null);
    try {
      const source_urls = scoutSources
        .split(/\n|,/) 
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.post("/admin/vacancies/scout", {
        query: scoutQuery,
        source_urls,
        include_known_operators: scoutIncludeKnown,
        max_known_operators: 10,
        max_jobs_per_source: 25,
        max_candidates: 160,
      });
      const withIds = {
        ...(r.data || {}),
        candidates: (r.data?.candidates || []).map((c, i) => ({
          ...c,
          _scout_id: `${i}-${c.apply_url || c.title || "candidate"}`,
        })),
      };
      setScoutResult(withIds);
      toast.success(`Scout found ${r.data.new_candidates} new candidate${r.data.new_candidates === 1 ? "" : "s"} (${r.data.existing} already listed)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scout run failed");
    } finally {
      setScoutRunning(false);
    }
  };

  const verifyScoutCandidateLink = async (candidate) => {
    const key = candidate._scout_id || candidate.apply_url || candidate.title || "candidate";
    if (!candidate?.apply_url) {
      toast.error("No candidate URL to verify");
      return;
    }
    setScoutVerifyingKey(key);
    try {
      const r = await api.post("/admin/vacancies/verify-link", { url: candidate.apply_url });
      const payload = r.data || {};
      setScoutResult((prev) => {
        if (!prev?.candidates) return prev;
        const nextCandidates = prev.candidates.map((c) => {
          const same = (c._scout_id || "") === (candidate._scout_id || "");
          if (!same) return c;
          return {
            ...c,
            apply_url: payload.verified_url || c.apply_url,
            employer_name: c.employer_name || payload.inferred_employer || "",
            link_verify_status: payload.status || "unchanged",
          };
        });
        return { ...prev, candidates: nextCandidates };
      });
      if (payload.changed) {
        toast.success("Official destination link resolved");
      } else {
        toast.success("Link verified — no better redirect found");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link verification failed");
    } finally {
      setScoutVerifyingKey("");
    }
  };

  const quickAddFromScout = (candidate) => {
    setExt((prev) => ({
      ...prev,
      apply_url: candidate.apply_url || "",
      title: candidate.title || "",
      employer_name: candidate.employer_name || "",
      location: candidate.location || "",
      description: candidate.description || prev.description || "",
    }));
    setTab("quickadd");
    toast.success("Quick-Add prefilled from scout candidate");
  };

  const toggleFeature = async (v) => {
    await api.put(`/admin/vacancies/${v.id}/feature`, { featured: !v.featured });
    toast.success(v.featured ? "Removed from featured" : "Promoted to featured");
    refresh();
  };

  const removeVacancy = async (id) => {
    if (!window.confirm("Delete this vacancy?")) return;
    await api.delete(`/employer/vacancies/${id}`);
    toast.success("Deleted"); refresh();
  };

  const previewNewsletter = async () => {
    const r = await api.post("/admin/newsletter/preview", { intro: nlIntro, extra_urls: nlExtra });
    setNlPreview(r.data);
    toast.success(`Preview built: ${r.data.vacancy_count} vacancies, ${r.data.extra_links.length} extra links`);
  };

  const sendNewsletter = async () => {
    if (!window.confirm(nlTest ? `Send TEST to ${nlTest}?` : `Send to ALL ${stats.subscribers || 0} subscribers?`)) return;
    try {
      const r = await api.post("/admin/newsletter/send", {
        subject: nlSubject, intro: nlIntro, extra_urls: nlExtra,
        test_email: nlTest || null,
      });
      toast.success(`Sent: ${r.data.sent} ${r.data.failed ? `(${r.data.failed} failed)` : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  const generateLinkedinPost = async () => {
    setLinkedinLoading(true);
    try {
      const r = await api.get("/admin/linkedin-post/generate");
      setLinkedinPost(r.data);
      toast.success("LinkedIn post generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate LinkedIn post");
    } finally {
      setLinkedinLoading(false);
    }
  };

  const copyLinkedinPost = async () => {
    if (!linkedinPost?.post_text_with_hashtags) {
      toast.error("Generate a LinkedIn post first");
      return;
    }
    try {
      await navigator.clipboard.writeText(linkedinPost.post_text_with_hashtags);
      toast.success("LinkedIn post copied");
    } catch {
      toast.error("Clipboard copy failed");
    }
  };

  const loadInappThread = async (threadId) => {
    try {
      const r = await api.get(`/admin/inapp/threads/${threadId}/messages`);
      setInappActiveThread(r.data.thread);
      setInappMessages(r.data.messages || []);
      await api.post(`/admin/inapp/threads/${threadId}/read`, {});
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load thread");
    }
  };

  const sendInappNotification = async () => {
    if (!inappSubject.trim()) { toast.error("Add a subject"); return; }
    if (!inappBody.trim()) { toast.error("Add a message body"); return; }
    if (inappMode === "individual" && !inappRecipientEmail.trim()) { toast.error("Add recipient email"); return; }
    if (inappMode === "talent_pool_stage" && !inappStage.trim()) { toast.error("Select a talent-pool stage"); return; }

    setInappSending(true);
    try {
      const r = await api.post("/admin/inapp/send", {
        subject: inappSubject,
        body: inappBody,
        format: inappFormat,
        recipient_mode: inappMode,
        recipient_email: inappRecipientEmail.trim() || null,
        talent_pool_stage: inappStage.trim() || null,
      });
      toast.success(`Notification sent to ${r.data.created} recipient(s)`);
      setInappSubject("");
      setInappBody("");
      setInappRecipientEmail("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send notification");
    } finally {
      setInappSending(false);
    }
  };

  const replyInappThread = async () => {
    if (!inappActiveThread?.id) return;
    if (!inappReplyBody.trim()) { toast.error("Reply cannot be empty"); return; }
    setInappReplySending(true);
    try {
      await api.post(`/admin/inapp/threads/${inappActiveThread.id}/reply`, {
        body: inappReplyBody,
        format: "text",
      });
      setInappReplyBody("");
      await loadInappThread(inappActiveThread.id);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reply failed");
    } finally {
      setInappReplySending(false);
    }
  };

  const archiveInappThread = async (threadId, archived = true) => {
    try {
      await api.post(`/admin/inapp/threads/${threadId}/archive`, { archived });
      if (inappActiveThread?.id === threadId) {
        setInappActiveThread(null);
        setInappMessages([]);
      }
      refresh();
      toast.success(archived ? "Archived" : "Moved to inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update archive state");
    }
  };

  const toggleInappSelected = (threadId) => {
    setInappSelectedIds((prev) => (
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    ));
  };

  const bulkArchiveInapp = async (archived = true) => {
    if (!inappSelectedIds.length) {
      toast.error("Select at least one thread");
      return;
    }
    try {
      const r = await api.post("/admin/inapp/threads/archive-bulk", {
        thread_ids: inappSelectedIds,
        archived,
      });
      if (inappActiveThread && inappSelectedIds.includes(inappActiveThread.id)) {
        setInappActiveThread(null);
        setInappMessages([]);
      }
      setInappSelectedIds([]);
      refresh();
      toast.success(`${r.data.updated} thread(s) updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk archive failed");
    }
  };

  useEffect(() => {
    generateLinkedinPost();
  }, []);

  const vacancyLinkCounts = vacancies.reduce((acc, v) => {
    const q = getVacancyLinkQuality(v);
    acc[q] = (acc[q] || 0) + 1;
    return acc;
  }, { native: 0, resolved: 0, fallback: 0 });

  const filteredVacancies = vacancies.filter((v) => {
    const q = getVacancyLinkQuality(v);
    if (vacancyLinkFilter === "all") return true;
    if (vacancyLinkFilter === "native") return q === "native";
    if (vacancyLinkFilter === "resolved") return q === "resolved";
    if (vacancyLinkFilter === "fallback") return q === "fallback";
    return true;
  });

  const scoutCandidatesFiltered = (scoutResult?.candidates || []).filter((c) => {
    const isNew = c.cross_ref_status === "new_candidate";
    if (scoutOnlyNew && !isNew) return false;
    const score = Number(c.relevance_score || 0);
    return score >= Number(scoutMinScore || 0);
  });

  const addDepot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/depots", {
        ...depot,
        lat: parseFloat(depot.lat),
        lng: parseFloat(depot.lng),
        roles: depot.roles.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Depot added");
      setDepot({ name: "", employer_name: "", address: "", postcode: "", lat: "", lng: "", roles: "" });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const removeDepot = async (id) => {
    if (!window.confirm("Remove depot?")) return;
    await api.delete(`/admin/depots/${id}`);
    toast.success("Removed"); refresh();
  };

  const submitExt = async (e) => {
    e.preventDefault();
    if (!ext.apply_url.startsWith("http")) { toast.error("URL must start with http(s)://"); return; }
    try {
      await api.post("/admin/vacancies/external", {
        ...ext,
        salary_min: ext.salary_min ? parseInt(ext.salary_min) : null,
        salary_max: ext.salary_max ? parseInt(ext.salary_max) : null,
        expiry_date: ext.expiry_date || null,
      });
      toast.success("Live link added — visible on /vacancies");
      setExt({ apply_url: "", title: "", employer_name: "", role_category: ext.role_category,
        location: "", region: "", salary_min: "", salary_max: "", employment_type: "full-time",
        description: "", expiry_date: "", featured: false });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
  };

  const addFeed = async (e) => {
    e.preventDefault();
    if (!newFeed.url.startsWith("http")) { toast.error("URL must start with http(s)://"); return; }
    try {
      await api.post("/admin/job-feeds", newFeed);
      toast.success("Feed added. Click Sync to import vacancies.");
      setNewFeed({ name: "", url: "", employer_hint: "", auto_feature: false, active: true });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const syncFeed = async (id) => {
    setFeedSyncing((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.post(`/admin/job-feeds/${id}/sync`);
      toast.success(`Found ${r.data.total_found || 0} · imported ${r.data.inserted} · skipped ${r.data.skipped}`);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setFeedSyncing((s) => ({ ...s, [id]: false })); }
  };

  const syncAllFeeds = async () => {
    setFeedSyncing((s) => ({ ...s, all: true }));
    try {
      const r = await api.post("/admin/job-feeds/sync-all");
      toast.success(`Synced ${r.data.feeds} feeds — imported ${r.data.inserted}, skipped ${r.data.skipped}`);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setFeedSyncing((s) => ({ ...s, all: false })); }
  };

  const removeFeed = async (id) => {
    if (!window.confirm("Remove this feed? Vacancies it imported stay in place.")) return;
    await api.delete(`/admin/job-feeds/${id}`);
    toast.success("Feed removed"); refresh();
  };

  const runGeocodeBackfill = async () => {
    if (!window.confirm("Run geocode backfill on all visible vacancies missing coordinates? This sends polite requests to Nominatim and may take a minute or two.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await api.post("/admin/vacancies/geocode-backfill");
      setBackfillResult(r.data);
      const { updated, skipped, checked } = r.data;
      if (checked === 0) {
        toast.success("All vacancies already have coordinates — nothing to do.");
      } else {
        toast.success(`Geocoded ${updated} of ${checked} vacancies (${skipped} skipped — no usable location text).`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const runCleanupDescriptions = async () => {
    if (!window.confirm("Run description cleanup? This decodes any HTML entities, strips tags from titles & descriptions, and DELETES any non-UK / foreign-currency postings that slipped through. Proceed?")) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const r = await api.post("/admin/vacancies/cleanup-descriptions");
      setCleanResult(r.data);
      const { cleaned, deleted_non_uk, unchanged } = r.data;
      toast.success(`Cleaned ${cleaned} · Removed ${deleted_non_uk} non-UK · ${unchanged} already clean`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const setApproval = async (eid, approved) => {
    try {
      await api.put(`/admin/employers/${eid}/approval`, { approved });
      toast.success(approved ? "Employer approved — they can now post vacancies" : "Employer access revoked");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Admin-create trial employer account
  const [newEmp, setNewEmp] = useState({
    email: "", company_name: "", contact_name: "", password: "", website: "", send_welcome_email: true,
  });
  const [creatingEmp, setCreatingEmp] = useState(false);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setNewEmp((prev) => ({ ...prev, password: p }));
  };

  const createTrialEmployer = async (e) => {
    e.preventDefault();
    if (!newEmp.email || !newEmp.company_name || !newEmp.password) {
      toast.error("Email, company name and password are required");
      return;
    }
    if (newEmp.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setCreatingEmp(true);
    try {
      await api.post("/admin/employers", newEmp);
      toast.success(`Trial account created for ${newEmp.company_name}${newEmp.send_welcome_email ? " — welcome email sent" : ""}`);
      setNewEmp({ email: "", company_name: "", contact_name: "", password: "", website: "", send_welcome_email: true });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setCreatingEmp(false);
    }
  };

  const resetEmployerPassword = async (eid, email) => {
    const pw = window.prompt(`Set a new password for ${email} (min 8 chars).\n\nThey will be emailed the new password (unless you blank this dialogue).`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/admin/employers/${eid}/reset-password`, { password: pw, notify: true });
      toast.success(`Password reset for ${email} — welcome email sent`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  const toggleEmployerFeatured = async (eid, current) => {
    try {
      await api.put(`/admin/employers/${eid}/featured`, { featured: !current });
      toast.success(!current ? "Employer featured — will appear on homepage" : "Featured flag removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const availableTalentStages = Array.from(
    new Set((talentMembers || []).map((m) => (m.talent_pool_stage || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const inappUnreadCount = (inappThreads || []).filter((t) => !t.admin_read_at).length;
  const inappArchivedCount = (inappArchivedThreads || []).length;
  const inappSearchNormalized = inappSearch.trim().toLowerCase();
  const matchesInappSearch = (t) => {
    if (!inappSearchNormalized) return true;
    const haystack = [
      t.subject,
      t.recipient_label,
      t.recipient_email,
      t.last_message_preview,
    ].join(" ").toLowerCase();
    return haystack.includes(inappSearchNormalized);
  };
  const filteredInappThreads = (inappThreads || []).filter((t) => {
    if (inappUnreadOnly && t.admin_read_at) return false;
    return matchesInappSearch(t);
  });
  const filteredInappArchivedThreads = (inappArchivedThreads || []).filter(matchesInappSearch);

  const buildCommsPayload = ({ useTestEmail = false } = {}) => ({
    subject: subMsgSubject,
    body: subMsgBody,
    format: subMsgFormat,
    recipient_mode: subMsgRecipientMode,
    recipient_email: subMsgRecipientEmail.trim() || null,
    talent_pool_stage: subMsgTalentStage.trim() || null,
    external_emails: subMsgExternalEmails,
    test_email: useTestEmail ? (subMsgTestEmail.trim() || null) : null,
  });

  const previewSubscriberNotification = async () => {
    if (!subMsgSubject.trim()) { toast.error("Add a subject"); return; }
    if (!subMsgBody.trim()) { toast.error("Add a message body"); return; }
    if ((subMsgRecipientMode === "individual" || subMsgRecipientMode === "external_single") && !subMsgRecipientEmail.trim()) {
      toast.error("Add a recipient email");
      return;
    }
    if (subMsgRecipientMode === "talent_pool_stage" && !subMsgTalentStage.trim()) {
      toast.error("Select a talent-pool stage");
      return;
    }
    if (subMsgRecipientMode === "external_group_bcc" && !subMsgExternalEmails.trim()) {
      toast.error("Add one or more external emails");
      return;
    }

    setSubMsgPreviewing(true);
    try {
      const r = await api.post("/admin/communications/preview", buildCommsPayload());
      setSubMsgPreview(r.data);
      toast.success(`Preview ready for ${r.data.recipient_count} recipient(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setSubMsgPreviewing(false);
    }
  };

  const sendSubscriberNotification = async ({ recipientEmail = null, useTestEmail = false } = {}) => {
    if (!subMsgSubject.trim()) { toast.error("Add a subject"); return; }
    if (!subMsgBody.trim()) { toast.error("Add a message body"); return; }
    if (useTestEmail && !subMsgTestEmail.trim()) { toast.error("Add a test email"); return; }
    if ((subMsgRecipientMode === "individual" || subMsgRecipientMode === "external_single") && !subMsgRecipientEmail.trim() && !recipientEmail) {
      toast.error("Add a recipient email");
      return;
    }
    if (subMsgRecipientMode === "talent_pool_stage" && !subMsgTalentStage.trim()) {
      toast.error("Select a talent-pool stage");
      return;
    }
    if (subMsgRecipientMode === "external_group_bcc" && !subMsgExternalEmails.trim()) {
      toast.error("Add one or more external emails");
      return;
    }

    const modeLabel = {
      individual: "individual recipient",
      employer_verified: "verified employers",
      employer_unverified: "unverified employers",
      talent_pool_all: "all talent-pool members",
      talent_pool_stage: `talent-pool stage: ${subMsgTalentStage || "(unset)"}`,
      subscribers_all: "all subscribers",
      external_single: "single external recipient",
      external_group_bcc: "external group (BCC)",
    }[subMsgRecipientMode] || subMsgRecipientMode;

    const confirmLabel = useTestEmail
      ? `Send TEST to ${subMsgTestEmail} using ${modeLabel}?`
      : recipientEmail
        ? `Send to ${recipientEmail}?`
        : `Send to ${modeLabel}?`;
    if (!window.confirm(confirmLabel)) return;

    setSubMsgSending(true);
    try {
      const payload = buildCommsPayload({ useTestEmail });
      if (recipientEmail) {
        payload.recipient_mode = "individual";
        payload.recipient_email = recipientEmail;
      }
      const r = await api.post("/admin/communications/send", payload);
      const reasonText = (r.data.failure_reasons || []).join(" | ");
      const fallbackText = r.data.bcc_fallback_used ? " · BCC fallback: individual sends" : "";
      const limitText = r.data.rate_limited ? " · Sending paused by provider limit" : "";
      toast.success(`Sent ${r.data.sent}${r.data.failed ? ` (${r.data.failed} failed)` : ""}${fallbackText}${limitText}${reasonText ? ` · ${reasonText}` : ""}`);
      setSubMsgPreview((prev) => prev ? { ...prev, recipient_count: r.data.recipient_count } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Notification send failed");
    } finally {
      setSubMsgSending(false);
    }
  };

  const pretendAs = async (uid, route) => {
    try {
      const r = await api.post("/admin/impersonate", { user_id: uid });
      setToken(r.data.access_token);
      window.location.href = route;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start pretend session");
    }
  };

  const seedPretendCandidate = async () => {
    const email = window.prompt("Candidate email (optional, leave blank for auto-generated)", "") || "";
    const firstName = window.prompt("First name", "Test") || "Test";
    const lastName = window.prompt("Last name", "Candidate") || "Candidate";
    const rolesRaw = window.prompt("Target roles (comma-separated slugs)", "train-driver,conductor") || "";
    const preferredLocations = window.prompt("Preferred locations", "Manchester, Leeds") || "";
    const postcode = window.prompt("Postcode", "M1 2AB") || "";
    const password = window.prompt("Password (optional, leave blank to auto-generate)", "") || "";
    const stage = window.prompt("Talent-pool stage", "stage_two_behavioural_profiling") || "stage_two_behavioural_profiling";
    const adminStatus = window.prompt("Admin status", "active") || "active";
    const notes = window.prompt("Notes", "Test candidate created from admin dashboard") || "";

    const targetRoles = rolesRaw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      email: email.trim() || null,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      target_roles: targetRoles,
      preferred_locations: preferredLocations.trim() || null,
      postcode: postcode.trim() || null,
      password: password.trim() || null,
      talent_pool_stage: stage.trim() || null,
      talent_pool_admin_status: adminStatus.trim() || null,
      notes: notes.trim() || null,
    };

    try {
      const r = await api.post("/admin/talent-pool/seed-pretend", payload);
      toast.success(`Test candidate ready: ${r.data.email} (password: ${r.data.password})`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create test candidate");
    }
  };

  const updateTalentDraft = (memberId, field, value) => {
    setTalentDrafts((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || {}),
        [field]: value,
      },
    }));
  };

  const saveTalentMember = async (memberId) => {
    const payload = talentDrafts[memberId] || {};
    try {
      await api.put(`/admin/talent-pool/members/${memberId}`, payload);
      toast.success("Talent-pool member updated");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const messageTalentMember = async (member) => {
    const subject = window.prompt(`Message subject for ${member.contact_name || member.email}`);
    if (!subject) return;
    const message = window.prompt("Message body");
    if (!message) return;
    try {
      await api.post(`/admin/talent-pool/members/${member.id}/message`, { subject, message });
      toast.success("Message sent");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Message failed");
    }
  };

  const toggleStageTwoAccess = async (member, enable) => {
    const label = member.contact_name || member.email;
    if (!window.confirm(`${enable ? "Enable" : "Disable"} Stage 2 access for ${label}?`)) return;
    try {
      await api.post(`/admin/talent-pool/stage-2/candidates/${member.id}/enable`, null, { params: { enabled: enable } });
      toast.success(`Stage 2 access ${enable ? "enabled" : "disabled"} for ${label}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update Stage 2 access");
    }
  };

  const resetStageTwoForCandidate = async (member) => {
    const reason = window.prompt("Reset reason", "verified platform failure");
    if (!reason) return;
    const incidentCategory = window.prompt("Incident category", "test_administration");
    if (!incidentCategory) return;
    const administratorNote = window.prompt("Administrator note (required)");
    if (!administratorNote || administratorNote.trim().length < 5) {
      toast.error("Administrator note is required (min 5 characters)");
      return;
    }
    const ok = window.confirm(`Authorise Stage 2 reset for ${member.contact_name || member.email}?`);
    if (!ok) return;

    try {
      await api.post(`/admin/talent-pool/stage-2/candidates/${member.id}/reset-latest`, {
        reason,
        administrator_note: administratorNote,
        incident_category: incidentCategory,
        supporting_information: null,
      });
      toast.success("Stage 2 reset authorised and audited");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset Stage 2 attempt");
    }
  };

  const STAT_CARDS = [
    { l: "Live vacancies", v: stats.live_vacancies },
    { l: "Featured", v: stats.featured },
    { l: "Employers", v: stats.employers },
    { l: "Pending", v: stats.pending_employers },
    { l: "Talent pool", v: stats.talent_pool_members },
    { l: "Ready pool", v: stats.talent_pool_ready },
  ];

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-navy text-bone">
        <div className="livery-stripe h-1" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Railway Careers UK" className="w-9 h-9 rounded-full bg-cream" />
            <div className="font-display font-bold tracking-tight text-sm">Admin Console</div>
          </Link>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={refresh} className="border-bone/30 text-bone hover:bg-cream/10"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={handleLogout} className="border-bone/30 text-bone hover:bg-cream/10" data-testid="admin-logout"><LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">Admin · {user?.email}</div>
        <h1 className="font-display font-extrabold text-ink text-3xl md:text-4xl tracking-tight mt-1">Operations console</h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Notifications: <span className="font-semibold text-ink">{inappUnreadCount}</span> unread
        </div>
        <div className="mt-3">
          <Link
            to="/admin/stage-2"
            className="inline-flex items-center rounded-full bg-navy px-4 py-2 text-sm font-semibold text-bone hover:bg-lime-600 hover:text-ink"
            data-testid="admin-stage2-link"
          >
            Open Stage 2 assessment dashboard
          </Link>
        </div>

        {(stats.pending_employers > 0 || stats.unverified_employers > 0) && (
          <div className="mt-6 rounded-lg border-2 border-amber bg-amber/10 p-5 flex items-start gap-4 flex-wrap" data-testid="admin-pending-banner">
            <Bell className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[260px]">
              <div className="font-display font-bold text-ink text-lg">
                {stats.pending_employers > 0 && (
                  <span>{stats.pending_employers} employer{stats.pending_employers === 1 ? "" : "s"} awaiting approval</span>
                )}
                {stats.pending_employers > 0 && stats.unverified_employers > 0 && <span> · </span>}
                {stats.unverified_employers > 0 && (
                  <span className="text-ink/70 text-base font-semibold">{stats.unverified_employers} email-unverified</span>
                )}
              </div>
              <p className="text-sm text-ink/80 mt-1">
                Email-confirmed registrations are ready for your review. Unverified accounts can't be approved until they click their email link.
              </p>
            </div>
            {stats.pending_employers > 0 && (
              <Button
                className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold"
                onClick={() => setTab("employers")}
                data-testid="admin-pending-review"
              >
                Review now →
              </Button>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAT_CARDS.map((s) => (
            <div key={s.l} className="bg-card border border-border rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
              <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v ?? "—"}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-amber/50 bg-amber/10 p-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-ink">New cleanup tool available</div>
            <p className="text-sm text-ink/80 mt-1">Manage expired or broken live vacancies from the Feed Debug tab. Use the vacancy cleanup tool to keep the public board fresh.</p>
          </div>
          <Button className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" onClick={() => setTab("feeddebug")} data-testid="admin-cleanup-cta">
            Go to Feed Debug
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-card border border-border w-full justify-start">
            <div className="px-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground self-center">Hiring Ops</div>
            <TabsTrigger value="vacancies" data-testid="admin-tab-vacancies">Vacancies</TabsTrigger>
            <TabsTrigger value="quickadd" data-testid="admin-tab-quickadd">Quick-Add Link</TabsTrigger>
            <TabsTrigger value="feeds" data-testid="admin-tab-feeds">Job Feeds</TabsTrigger>
            <TabsTrigger value="bulksync" data-testid="admin-tab-bulksync">
              Bulk Sync
            </TabsTrigger>
            <TabsTrigger value="aiscout" data-testid="admin-tab-aiscout">AI Scout</TabsTrigger>
            <TabsTrigger value="feeddebug" data-testid="admin-tab-feeddebug">Feed Debug</TabsTrigger>
            <TabsTrigger value="sensecheck" data-testid="admin-tab-sensecheck">Sense Check</TabsTrigger>
            <TabsTrigger value="bulkupload" data-testid="admin-tab-bulkupload">Bulk Upload</TabsTrigger>
            <TabsTrigger value="depots" data-testid="admin-tab-depots">Depots</TabsTrigger>

            <div className="px-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground self-center">People</div>
            <TabsTrigger value="employers" data-testid="admin-tab-employers">
              Employers{stats.pending_employers > 0 ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber text-ink text-[10px] font-bold">{stats.pending_employers}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="talentpool" data-testid="admin-tab-talentpool">Talent Pool</TabsTrigger>

            <div className="px-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground self-center">Comms</div>
            <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter">Newsletter + LinkedIn</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">Email Campaigns</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications">
              Notifications
              {inappUnreadCount > 0 ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber text-ink text-[10px] font-bold">{inappUnreadCount}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="notifications-archive" data-testid="admin-tab-notifications-archive">
              Notifications Archive
              {inappArchivedCount > 0 ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-paper text-ink text-[10px] font-bold">{inappArchivedCount}</span> : null}
            </TabsTrigger>

            <div className="px-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground self-center">CRM</div>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Sales requests</TabsTrigger>
          </TabsList>

          <TabsContent value="vacancies" className="mt-6">
            <div className="mb-3 rounded-lg border border-border bg-card p-3" data-testid="admin-vac-link-filter">
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Adzuna link quality</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${vacancyLinkFilter === "all" ? "bg-navy text-bone border-navy" : "border-border text-ink hover:border-amber"}`}
                  onClick={() => setVacancyLinkFilter("all")}
                  data-testid="admin-vac-filter-all"
                >
                  All ({vacancies.length})
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${vacancyLinkFilter === "fallback" ? "bg-red-100 text-red-900 border-red-300" : "border-border text-ink hover:border-red-300"}`}
                  onClick={() => setVacancyLinkFilter("fallback")}
                  data-testid="admin-vac-filter-fallback"
                >
                  Adzuna fallback ({vacancyLinkCounts.fallback || 0})
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${vacancyLinkFilter === "resolved" ? "bg-lime-100 text-lime-900 border-lime-300" : "border-border text-ink hover:border-lime-300"}`}
                  onClick={() => setVacancyLinkFilter("resolved")}
                  data-testid="admin-vac-filter-resolved"
                >
                  Official link resolved ({vacancyLinkCounts.resolved || 0})
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${vacancyLinkFilter === "native" ? "bg-sky/10 text-ink border-sky/40" : "border-border text-ink hover:border-sky/40"}`}
                  onClick={() => setVacancyLinkFilter("native")}
                  data-testid="admin-vac-filter-native"
                >
                  Direct/non-Adzuna ({vacancyLinkCounts.native || 0})
                </button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              <div className="md:hidden divide-y divide-border">
                {filteredVacancies.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">No vacancies match this link filter.</div>
                )}
                {filteredVacancies.map((v) => {
                  const quality = getVacancyLinkQuality(v);
                  const qualityBadge = quality === "resolved"
                    ? "bg-lime-100 text-lime-900 border-lime-300"
                    : quality === "fallback"
                      ? "bg-red-100 text-red-900 border-red-300"
                      : "bg-sky/10 text-ink border-sky/40";
                  const qualityLabel = quality === "resolved"
                    ? "Official link resolved"
                    : quality === "fallback"
                      ? "Adzuna fallback"
                      : "Direct/non-Adzuna";
                  return (
                  <div key={`mobile-vac-${v.id}`} className="p-4 space-y-3" data-testid={`admin-vac-mobile-${v.id}`}>
                    <div className="font-semibold text-ink">{v.title}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Employer</div>
                        <div className="text-ink mt-0.5">{v.employer_name || "—"}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Role</div>
                        <div className="text-ink mt-0.5">{v.role_category || "—"}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="uppercase tracking-wider text-muted-foreground">Location</div>
                        <div className="text-ink mt-0.5">{v.location || "—"}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="uppercase tracking-wider text-muted-foreground">Apply link source</div>
                        <div className="mt-0.5">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${qualityBadge}`}>{qualityLabel}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" variant={v.featured ? "default" : "outline"} onClick={() => toggleFeature(v)} className={v.featured ? "bg-amber text-ink hover:bg-amber-600" : ""} data-testid={`admin-feature-mobile-${v.id}`}>
                        <Star className={`w-3.5 h-3.5 mr-1.5 ${v.featured ? "fill-navy" : ""}`} /> {v.featured ? "Featured" : "Feature"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removeVacancy(v.id)} data-testid={`admin-vac-delete-mobile-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>

              <table className="hidden md:table w-full text-sm">
                <thead className="bg-sand"><tr className="text-left">
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Title</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Employer</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Role</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Location</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Apply link</th>
                  <th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {filteredVacancies.map((v) => {
                    const quality = getVacancyLinkQuality(v);
                    const qualityBadge = quality === "resolved"
                      ? "bg-lime-100 text-lime-900 border-lime-300"
                      : quality === "fallback"
                        ? "bg-red-100 text-red-900 border-red-300"
                        : "bg-sky/10 text-ink border-sky/40";
                    const qualityLabel = quality === "resolved"
                      ? "Official link resolved"
                      : quality === "fallback"
                        ? "Adzuna fallback"
                        : "Direct/non-Adzuna";
                    return (
                    <tr key={v.id} className="border-t border-border" data-testid={`admin-vac-${v.id}`}>
                      <td className="px-4 py-3 font-semibold text-ink">{v.title}</td>
                      <td className="px-4 py-3 text-muted-foreground">{v.employer_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{v.role_category}</td>
                      <td className="px-4 py-3 text-muted-foreground">{v.location}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${qualityBadge}`}>{qualityLabel}</span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant={v.featured ? "default" : "outline"} onClick={() => toggleFeature(v)} className={v.featured ? "bg-amber text-ink hover:bg-amber-600" : ""} data-testid={`admin-feature-${v.id}`}>
                          <Star className={`w-3.5 h-3.5 mr-1.5 ${v.featured ? "fill-navy" : ""}`} /> {v.featured ? "Featured" : "Feature"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeVacancy(v.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="quickadd" className="mt-6 space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 max-w-3xl">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-amber/20 text-ink flex items-center justify-center font-display font-extrabold">URL</div>
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Add a real live job link</div>
                  <p className="text-sm text-muted-foreground mt-1">Paste a URL from any UK rail employer's careers site. Fill in the basics. The vacancy appears on /vacancies straight away with an "Apply" button that deep-links to the original page.</p>
                </div>
              </div>
              <form onSubmit={submitExt} className="mt-6 grid md:grid-cols-2 gap-4" data-testid="quickadd-form">
                <div className="md:col-span-2">
                  <Label>Apply URL <span className="text-destructive">*</span></Label>
                  <Input required type="url" placeholder="https://careers.networkrail.co.uk/job/..." className="mt-1 font-mono text-sm" value={ext.apply_url} onChange={(e) => setExt({ ...ext, apply_url: e.target.value })} data-testid="quickadd-url" />
                </div>
                <div className="md:col-span-2">
                  <Label>Job title <span className="text-destructive">*</span></Label>
                  <Input required className="mt-1" placeholder="Trainee Train Driver — Manchester Piccadilly" value={ext.title} onChange={(e) => setExt({ ...ext, title: e.target.value })} data-testid="quickadd-title" />
                </div>
                <div>
                  <Label>Employer <span className="text-destructive">*</span></Label>
                  <Input required className="mt-1" placeholder="Network Rail" value={ext.employer_name} onChange={(e) => setExt({ ...ext, employer_name: e.target.value })} data-testid="quickadd-employer" />
                </div>
                <div>
                  <Label>Role category</Label>
                  <Select value={ext.role_category} onValueChange={(v) => setExt({ ...ext, role_category: v })}>
                    <SelectTrigger className="mt-1" data-testid="quickadd-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["train-driver","conductor","depot-driver","fitter","engineering","signalling","customer-service","graduate","apprentice","other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location <span className="text-destructive">*</span></Label>
                  <Input required className="mt-1" placeholder="Manchester" value={ext.location} onChange={(e) => setExt({ ...ext, location: e.target.value })} data-testid="quickadd-location" />
                </div>
                <div>
                  <Label>Region</Label>
                  <Input className="mt-1" placeholder="North West" value={ext.region} onChange={(e) => setExt({ ...ext, region: e.target.value })} data-testid="quickadd-region" />
                </div>
                <div>
                  <Label>Salary min (£)</Label>
                  <Input type="number" className="mt-1" value={ext.salary_min} onChange={(e) => setExt({ ...ext, salary_min: e.target.value })} />
                </div>
                <div>
                  <Label>Salary max (£)</Label>
                  <Input type="number" className="mt-1" value={ext.salary_max} onChange={(e) => setExt({ ...ext, salary_max: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label>Short description (optional)</Label>
                  <Textarea className="mt-1 min-h-[80px]" placeholder="Two-sentence summary. If blank, a default 'View on …' line is shown." value={ext.description} onChange={(e) => setExt({ ...ext, description: e.target.value })} />
                </div>
                <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>End date</Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={ext.expiry_date}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setExt({ ...ext, expiry_date: e.target.value })}
                      data-testid="quickadd-expiry"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Vacancy is auto-removed from the site after this date. Leave blank to default to +60 days.
                    </p>
                  </div>
                  <div>
                    <Label>Quick presets</Label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {[
                        { d: 14, label: "2 weeks" },
                        { d: 30, label: "30 days" },
                        { d: 60, label: "60 days" },
                        { d: 90, label: "90 days" },
                      ].map((p) => (
                        <button
                          key={p.d}
                          type="button"
                          className="px-3 py-1.5 rounded-md border border-border text-xs hover:border-amber hover:text-ink transition-colors"
                          onClick={() => {
                            const d = new Date();
                            d.setDate(d.getDate() + p.d);
                            setExt({ ...ext, expiry_date: d.toISOString().split("T")[0] });
                          }}
                          data-testid={`quickadd-expiry-${p.d}d`}
                        >
                          +{p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={ext.featured} onChange={(e) => setExt({ ...ext, featured: e.target.checked })} data-testid="quickadd-featured" />
                  <span>Pin to top as a Featured listing</span>
                </label>
                <Button type="submit" className="md:col-span-2 bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="quickadd-submit">Publish vacancy live</Button>
              </form>
              <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4 space-y-2">
                <div><strong className="text-ink">Tip:</strong> bulk-imports? Add them one at a time here, then use Vacancies tab to feature the strongest 2–3 each week. The Newsletter generator pulls them automatically.</div>
                <div><strong className="text-ink">Webitrent operators (c2c, Greater Anglia, etc.):</strong> their portal blocks third-party deep links. Paste the operator's <em>careers homepage</em> URL instead of a specific vacancy URL — applicants can search the role from there.</div>
              </div>
            </div>

            {/* Maintenance — geocode + cleanup */}
            <div className="bg-card border border-border rounded-lg p-6" data-testid="geocode-backfill-card">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-amber/15 text-ink flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Vacancy maintenance</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    One-shot tools for cleaning legacy vacancies. New listings imported via Adzuna, sitemap-crawler or Quick-Add are now auto-geocoded and auto-sanitised on save, so you'll rarely need to run these.
                  </p>

                  {/* Geocode */}
                  <div className="mt-5 border-t border-border pt-4">
                    <div className="font-semibold text-ink text-sm">Geocode backfill</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Adds map coordinates to any vacancies missing lat/lng. Uses postcodes.io and Nominatim (OSM).</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        onClick={runGeocodeBackfill}
                        disabled={backfilling}
                        className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold"
                        data-testid="geocode-backfill-run"
                      >
                        {backfilling ? "Geocoding…" : "Run geocode backfill"}
                      </Button>
                      {backfillResult && (
                        <div className="text-sm text-muted-foreground" data-testid="geocode-backfill-result">
                          Checked <strong className="text-ink">{backfillResult.checked}</strong> · Updated <strong className="text-ink">{backfillResult.updated}</strong> · Skipped <strong className="text-ink">{backfillResult.skipped}</strong>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description cleanup */}
                  <div className="mt-5 border-t border-border pt-4">
                    <div className="font-semibold text-ink text-sm">Description cleanup &amp; non-UK purge</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Decodes any double-encoded HTML entities (e.g. <code className="text-amber-700 font-mono">&amp;lt;p&amp;gt;</code>), strips HTML tags from titles &amp; descriptions, normalises smart quotes, and <strong className="text-ink">deletes</strong> any postings paid in foreign currency or based outside the UK (e.g. Philippines roles in US$).
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        onClick={runCleanupDescriptions}
                        disabled={cleaning}
                        className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold"
                        data-testid="cleanup-descriptions-run"
                      >
                        {cleaning ? "Cleaning…" : "Run description cleanup"}
                      </Button>
                      {cleanResult && (
                        <div className="text-sm text-muted-foreground" data-testid="cleanup-descriptions-result">
                          Cleaned <strong className="text-ink">{cleanResult.cleaned}</strong> · Removed non-UK <strong className="text-ink">{cleanResult.deleted_non_uk}</strong> · Already clean <strong className="text-ink">{cleanResult.unchanged}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bulksync" className="mt-6 space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-sky/15 text-ink flex items-center justify-center"><Zap className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Adzuna sync</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    Pull live UK rail vacancies from Adzuna's national jobs database in one click. We filter to known TOCs, infrastructure
                    managers and Tier-1 contractors, dedupe against what's already on your board, and deep-link to each operator's apply page.
                    Run this daily — your free tier covers 250 calls/month and a typical run uses 14–18.
                  </p>
                </div>
              </div>
              <div className="mt-5 grid md:grid-cols-[1fr_140px] gap-4">
                <div>
                  <Label>Search keywords (comma-separated)</Label>
                  <Input className="mt-1 font-mono text-sm" value={adzKw} onChange={(e) => setAdzKw(e.target.value)} data-testid="bulksync-adzuna-keywords" />
                </div>
                <div>
                  <Label>Pages per keyword</Label>
                  <Input type="number" min={1} max={5} className="mt-1" value={adzPages} onChange={(e) => setAdzPages(e.target.value)} data-testid="bulksync-adzuna-pages" />
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <Button onClick={runAdzuna} disabled={adzRunning} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="bulksync-adzuna-run">
                  {adzRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  {adzRunning ? "Syncing…" : "Run Adzuna sync"}
                </Button>
                {adzLast?.ran_at && (
                  <div className="text-xs text-muted-foreground font-mono">
                    Last run: {adzLast.ran_at?.slice(0, 16).replace("T", " ")} —{" "}
                    {adzLast.summary?.inserted ?? 0} inserted of {adzLast.summary?.matched_toc ?? 0} matched
                  </div>
                )}
              </div>
              {adzResult && (
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bulksync-adzuna-result">
                  {[
                    { l: "Fetched", v: adzResult.fetched, t: "from Adzuna" },
                    { l: "Matched TOCs", v: adzResult.matched_toc, t: "passed filter" },
                    { l: "Inserted", v: adzResult.inserted, t: "new on board" },
                    { l: "Skipped", v: adzResult.skipped, t: "duplicates / non-rail" },
                  ].map((s) => (
                    <div key={s.l} className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{s.t}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-amber/20 text-ink flex items-center justify-center"><Globe className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Sitemap crawler</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    For operators not on Adzuna: paste their careers website root, we'll auto-discover their <code className="bg-paper px-1 rounded">sitemap.xml</code>,
                    walk every job URL, and pull the JSON-LD JobPosting schema. Works on Workday, SAP SuccessFactors, iCIMS, Eploy, TribePad and most ATS-published sitemaps.
                  </p>
                </div>
              </div>
              <div className="mt-5 grid md:grid-cols-[1fr_240px] gap-4">
                <div>
                  <Label>Careers URL</Label>
                  <Input className="mt-1 font-mono text-sm" placeholder="https://careers.networkrail.co.uk" value={smapUrl} onChange={(e) => setSmapUrl(e.target.value)} data-testid="bulksync-sitemap-url" />
                </div>
                <div>
                  <Label>Employer name fallback</Label>
                  <Input className="mt-1" placeholder="Network Rail" value={smapHint} onChange={(e) => setSmapHint(e.target.value)} data-testid="bulksync-sitemap-hint" />
                </div>
              </div>
              <Button onClick={runSitemap} disabled={smapRunning} className="mt-5 bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="bulksync-sitemap-run">
                {smapRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                {smapRunning ? "Crawling…" : "Crawl sitemap"}
              </Button>
              {smapResult && !smapResult.error && (
                <div className="mt-5 space-y-3" data-testid="bulksync-sitemap-result">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { l: "Job URLs", v: smapResult.job_urls, t: "from sitemap" },
                      { l: "Fetched", v: smapResult.fetched, t: "pages" },
                      { l: "JSON-LD", v: smapResult.matched, t: "JobPostings found" },
                      { l: "Inserted", v: smapResult.inserted, t: "new on board" },
                    ].map((s) => (
                      <div key={s.l} className="bg-paper border border-border rounded p-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                        <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{s.t}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono break-all">
                    Sitemap: {smapResult.sitemaps_found?.[0] || "—"} · Candidates: {smapResult.candidate_urls}
                  </div>
                  {smapResult.matched === 0 && smapResult.fetched > 0 && (
                    <div className="text-xs text-amber-700 bg-amber/10 border border-amber/40 rounded p-3">
                      Sitemap found and pages were fetched, but none had JSON-LD JobPosting schema.
                      This site doesn't expose Google-for-Jobs structured data — try Adzuna for this operator instead.
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="aiscout" className="mt-6 space-y-6">
            <div className="bg-card border border-border rounded-lg p-6" data-testid="ai-scout-panel">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-lime-100 text-ink flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">AI Vacancy Scout</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    Discover likely rail vacancies from known operator careers pages plus optional forum/social/web sources, then cross-reference against vacancies already listed on Railway Careers UK.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Scout query</Label>
                  <Input className="mt-1" value={scoutQuery} onChange={(e) => setScoutQuery(e.target.value)} placeholder="e.g. signalling technician uk rail" data-testid="ai-scout-query" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={scoutIncludeKnown} onChange={(e) => setScoutIncludeKnown(e.target.checked)} data-testid="ai-scout-known-ops" />
                    <span>Include known TOC/FOC/infrastructure operators</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <Label>Extra source URLs (optional, one per line)</Label>
                  <Textarea
                    className="mt-1 min-h-[110px] font-mono text-xs"
                    value={scoutSources}
                    onChange={(e) => setScoutSources(e.target.value)}
                    placeholder={"https://www.railforums.co.uk/forums/\nhttps://www.linkedin.com/company/network-rail/posts/\nhttps://example.com/careers"}
                    data-testid="ai-scout-sources"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <Button onClick={runScout} disabled={scoutRunning} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="ai-scout-run">
                  {scoutRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {scoutRunning ? "Scanning…" : "Run scout"}
                </Button>
                <div className="text-xs text-muted-foreground">
                  Results are shortlist suggestions only. Always verify candidate links on official employer sites.
                </div>
              </div>
            </div>

            {scoutResult && (
              <div className="bg-card border border-border rounded-lg p-6" data-testid="ai-scout-results">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { l: "Discovered", v: scoutResult.discovered },
                    { l: "New candidates", v: scoutResult.new_candidates },
                    { l: "Already listed", v: scoutResult.existing },
                    { l: "Known ops scanned", v: scoutResult.sources_scanned?.known_operators ?? 0 },
                  ].map((s) => (
                    <div key={s.l} className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{s.v}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-lg border border-border bg-paper/40 p-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={scoutOnlyNew} onChange={(e) => setScoutOnlyNew(e.target.checked)} data-testid="ai-scout-filter-only-new" />
                    <span>Only new candidates</span>
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Label className="text-xs text-muted-foreground">Min AI score</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={scoutMinScore}
                      onChange={(e) => setScoutMinScore(Math.max(0, Math.min(100, parseInt(e.target.value || "0", 10))))}
                      className="w-20 h-8"
                      data-testid="ai-scout-filter-min-score"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Showing <span className="font-semibold text-ink">{scoutCandidatesFiltered.length}</span> of {(scoutResult.candidates || []).length} candidates
                  </div>
                </div>

                <div className="mt-4 border border-border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-sand">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Candidate</th>
                        <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Source</th>
                        <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Cross-ref</th>
                        <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">AI score</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scoutCandidatesFiltered.slice(0, 120).map((c, idx) => {
                        const listed = c.cross_ref_status === "already_listed";
                        const sourceKind = String(c.source_kind || "").toLowerCase();
                        const fromCareersSource = sourceKind === "careers";
                        const verifyKey = c._scout_id || `${c.apply_url || c.title || "candidate"}-${idx}`;
                        const verifying = scoutVerifyingKey === verifyKey;
                        const verifyStatus = String(c.link_verify_status || "").toLowerCase();
                        const linkVerified = verifyStatus === "resolved" || verifyStatus === "unchanged";
                        const quickAddAllowed = fromCareersSource || linkVerified;
                        return (
                          <tr key={c._scout_id || `${c.apply_url || c.title}-${idx}`} className="border-t border-border">
                            <td className="px-4 py-3 align-top">
                              <div className="font-semibold text-ink">{c.title || "Potential vacancy"}</div>
                              <div className="text-xs text-muted-foreground mt-1">{c.employer_name || "Unknown employer"}</div>
                              <a className="text-[11px] font-mono text-muted-foreground hover:text-ink break-all" href={c.apply_url} target="_blank" rel="noreferrer">{c.apply_url}</a>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground align-top">
                              <div>{String(c.source_kind || "web").replaceAll("_", " ")}</div>
                              <div className="mt-1 break-all">{c.source_origin || "—"}</div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              {listed ? (
                                <span className="inline-flex items-center rounded-full border border-lime-300 bg-lime-100 px-2 py-0.5 text-[11px] font-semibold text-lime-900">Already listed</span>
                              ) : (
                                <span className="inline-flex items-center rounded-full border border-amber/50 bg-amber/20 px-2 py-0.5 text-[11px] font-semibold text-ink">New candidate</span>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top">
                              <span className="inline-flex items-center rounded-full border border-sky/40 bg-sky/10 px-2 py-0.5 text-[11px] font-semibold text-ink">{c.relevance_score ?? 0}/100</span>
                              {verifyStatus && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center rounded-full border border-border bg-paper px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                    Link: {String(verifyStatus).replaceAll("_", " ")}
                                  </span>
                                </div>
                              )}
                              {!quickAddAllowed && !listed && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center rounded-full border border-amber/50 bg-amber/20 px-2 py-0.5 text-[10px] font-semibold text-ink">
                                    Verify link before Quick-Add
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right align-top space-y-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => verifyScoutCandidateLink(c)}
                                disabled={verifying}
                                data-testid={`ai-scout-verify-${idx}`}
                              >
                                {verifying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                                Verify official link
                              </Button>
                              {!listed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => quickAddFromScout(c)}
                                  disabled={!quickAddAllowed}
                                  title={quickAddAllowed ? "" : "Verify official link first for non-careers sources"}
                                  data-testid={`ai-scout-quickadd-${idx}`}
                                >
                                  Use in Quick-Add
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="feeddebug" className="mt-6">
            <FeedDebugPanel />
          </TabsContent>

          <TabsContent value="feeds" className="mt-6">
            <div className="grid lg:grid-cols-[420px_1fr] gap-6">
              <form onSubmit={addFeed} className="bg-card border border-border rounded-lg p-6 space-y-4 h-fit" data-testid="feed-add-form">
                <div>
                  <div className="font-display font-bold text-ink text-lg">Add a careers feed</div>
                  <p className="text-xs text-muted-foreground mt-1">Most UK rail operators expose JSON-LD <code className="bg-sand px-1 rounded">JobPosting</code> tags on their careers pages. Paste the index URL — we'll auto-import every live vacancy and re-import on each sync (deduped).</p>
                </div>
                <div><Label>Feed name</Label><Input required className="mt-1" placeholder="Network Rail — All vacancies" value={newFeed.name} onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })} data-testid="feed-name" /></div>
                <div><Label>Careers index URL</Label><Input required type="url" className="mt-1 font-mono text-xs" placeholder="https://careers.networkrail.co.uk/" value={newFeed.url} onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })} data-testid="feed-url" /></div>
                <div><Label>Employer (fallback)</Label><Input className="mt-1" placeholder="Network Rail" value={newFeed.employer_hint} onChange={(e) => setNewFeed({ ...newFeed, employer_hint: e.target.value })} data-testid="feed-employer" /></div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newFeed.auto_feature} onChange={(e) => setNewFeed({ ...newFeed, auto_feature: e.target.checked })} />
                  <span>Auto-feature imports from this feed</span>
                </label>
                <Button type="submit" className="w-full bg-navy text-bone hover:bg-lime-600 hover:text-ink" data-testid="feed-submit">Add feed</Button>
                <div className="text-[11px] text-muted-foreground border-t pt-3 leading-relaxed">
                  <strong className="text-ink">Tested with:</strong> Network Rail, GTR, SWR, ScotRail, LNER, Avanti West Coast, Northern, TransPennine Express. Sites without JSON-LD return 0 imports — try a more specific job-detail URL or use Quick-Add.
                </div>
              </form>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-ink">Active feeds</div>
                  <Button size="sm" onClick={syncAllFeeds} disabled={feedSyncing.all || feeds.length === 0} className="bg-amber text-ink hover:bg-amber-600 font-semibold" data-testid="feed-sync-all">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${feedSyncing.all ? "animate-spin" : ""}`} /> Sync all
                  </Button>
                </div>
                {feeds.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-12 text-center text-sm text-muted-foreground">
                    No feeds yet. Add a careers page URL on the left to start auto-importing.
                  </div>
                ) : feeds.map((f) => (
                  <div key={f.id} className="bg-card border border-border rounded-lg p-5" data-testid={`feed-row-${f.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-ink">{f.name}</div>
                        <a href={f.url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-muted-foreground hover:text-ink break-all">{f.url}</a>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                          <span className={`px-2 py-0.5 rounded uppercase tracking-wider font-semibold ${
                            f.last_sync_status === "ok" ? "bg-amber/30 text-ink" :
                            f.last_sync_status === "error" ? "bg-destructive/15 text-destructive" :
                            "bg-sand text-muted-foreground"
                          }`}>{f.last_sync_status || "pending"}</span>
                          {f.last_synced_at && <span className="text-muted-foreground">last sync {f.last_synced_at.slice(0, 16).replace("T", " ")}</span>}
                          {f.last_sync_inserted > 0 && <span className="text-ink font-mono">+{f.last_sync_inserted}</span>}
                          {f.auto_feature && <span className="bg-amber/20 text-ink px-2 py-0.5 rounded font-semibold">auto-feature</span>}
                        </div>
                        {f.last_sync_message && <div className="mt-2 text-xs text-muted-foreground">{f.last_sync_message}</div>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button size="sm" onClick={() => syncFeed(f.id)} disabled={feedSyncing[f.id]} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink" data-testid={`feed-sync-${f.id}`}>
                          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${feedSyncing[f.id] ? "animate-spin" : ""}`} /> Sync
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeFeed(f.id)} className="text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <AutoSendStatus />
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div><Label>Subject</Label><Input className="mt-1" value={nlSubject} onChange={(e) => setNlSubject(e.target.value)} data-testid="nl-subject" /></div>
                <div><Label>Intro paragraph</Label><Textarea className="mt-1 min-h-[80px]" value={nlIntro} onChange={(e) => setNlIntro(e.target.value)} data-testid="nl-intro" /></div>
                <div>
                  <Label>Extra URLs (paste — newline or comma separated)</Label>
                  <Textarea className="mt-1 min-h-[120px] font-mono text-xs" placeholder="https://… &#10;https://…" value={nlExtra} onChange={(e) => setNlExtra(e.target.value)} data-testid="nl-extra-urls" />
                  <div className="text-xs text-muted-foreground mt-1">These appear under "Around the industry" in the newsletter.</div>
                </div>
                <div>
                  <Label>Test email (optional)</Label>
                  <Input className="mt-1" type="email" placeholder="leave blank to send to all subscribers" value={nlTest} onChange={(e) => setNlTest(e.target.value)} data-testid="nl-test-email" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={previewNewsletter} variant="outline" className="border-white text-ink" data-testid="nl-preview"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
                  <Button onClick={sendNewsletter} className="bg-amber text-ink hover:bg-amber-600 font-semibold" data-testid="nl-send"><Send className="w-3.5 h-3.5 mr-1.5" /> {nlTest ? "Send test" : `Send to ${stats.subscribers || 0}`}</Button>
                </div>
                <div className="text-xs text-muted-foreground">Resend API key required to actually send. Otherwise the preview still works.</div>
              </div>
              <div className="bg-card border border-border rounded-lg p-2 overflow-auto" style={{ maxHeight: "640px" }}>
                {nlPreview ? <div dangerouslySetInnerHTML={{ __html: nlPreview.html }} /> : <div className="p-12 text-center text-sm text-muted-foreground">Click Preview to render the newsletter.</div>}
              </div>
            </div>

            <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="linkedin-post-generator">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Weekly LinkedIn post generator</div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    Auto-build a weekly LinkedIn post using current live vacancies, top role demand, and hiring activity.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateLinkedinPost}
                    disabled={linkedinLoading}
                    data-testid="linkedin-generate"
                  >
                    {linkedinLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    {linkedinLoading ? "Generating..." : "Generate this week"}
                  </Button>
                  <Button
                    type="button"
                    className="bg-amber text-ink hover:bg-amber-600 font-semibold"
                    onClick={copyLinkedinPost}
                    disabled={!linkedinPost?.post_text_with_hashtags}
                    data-testid="linkedin-copy"
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Copy post
                  </Button>
                </div>
              </div>

              {linkedinPost ? (
                <div className="mt-5 space-y-4">
                  <div className="grid sm:grid-cols-4 gap-3">
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Week commencing</div>
                      <div className="font-mono font-semibold text-ink mt-1">{linkedinPost.week_commencing}</div>
                    </div>
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Live roles</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{linkedinPost.stats?.live_vacancies ?? 0}</div>
                    </div>
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Hiring employers</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{linkedinPost.stats?.hiring_employers ?? 0}</div>
                    </div>
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Featured live</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{linkedinPost.stats?.featured_vacancies ?? 0}</div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Talent pool total</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{linkedinPost.stats?.talent_pool_total ?? 0}</div>
                    </div>
                    <div className="bg-paper border border-border rounded p-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Talent pool strong-fit</div>
                      <div className="font-mono font-bold text-ink text-2xl mt-1">{linkedinPost.stats?.talent_pool_great_fit ?? 0}</div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg bg-paper/40 p-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Role-fit snapshot</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {(linkedinPost.stats?.role_fit_metrics || []).length === 0 ? (
                        <span>No role-fit data yet.</span>
                      ) : (
                        (linkedinPost.stats?.role_fit_metrics || []).slice(0, 3).map((item) => (
                          <div key={item.role} className="mt-1">
                            <span className="text-ink font-semibold">{item.label}</span>
                            {`: ${item.live_jobs} live role${item.live_jobs === 1 ? "" : "s"}, ${item.talent_pool_fit} strong-fit candidate${item.talent_pool_fit === 1 ? "" : "s"}`}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="border border-border rounded-lg bg-background p-4">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Post preview</Label>
                    <Textarea
                      className="mt-2 min-h-[260px] font-mono text-sm"
                      value={linkedinPost.post_text_with_hashtags || ""}
                      onChange={(e) => setLinkedinPost({ ...linkedinPost, post_text_with_hashtags: e.target.value })}
                      data-testid="linkedin-post-preview"
                    />
                    <div className="mt-2 text-xs text-muted-foreground">You can edit the generated draft before posting to LinkedIn.</div>
                  </div>
                </div>
              ) : (
                <div className="mt-5 border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
                  Generate to create a ready-to-post weekly LinkedIn update.
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sensecheck" className="mt-6">
            <SenseCheckStatus />
          </TabsContent>

          <TabsContent value="bulkupload" className="mt-6">
            <BulkVacancyUpload />
          </TabsContent>

          <TabsContent value="depots" className="mt-6">
            <div className="grid lg:grid-cols-[360px_1fr] gap-6">
              <form onSubmit={addDepot} className="bg-card border border-border rounded-lg p-6 space-y-3" data-testid="depot-add-form">
                <div className="font-display font-bold text-ink">Add hiring depot</div>
                <div><Label>Name</Label><Input required className="mt-1" value={depot.name} onChange={(e) => setDepot({ ...depot, name: e.target.value })} /></div>
                <div><Label>Employer</Label><Input required className="mt-1" value={depot.employer_name} onChange={(e) => setDepot({ ...depot, employer_name: e.target.value })} /></div>
                <div><Label>Address</Label><Input className="mt-1" value={depot.address} onChange={(e) => setDepot({ ...depot, address: e.target.value })} /></div>
                <div><Label>Postcode</Label><Input className="mt-1 uppercase" value={depot.postcode} onChange={(e) => setDepot({ ...depot, postcode: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Lat</Label><Input required className="mt-1" value={depot.lat} onChange={(e) => setDepot({ ...depot, lat: e.target.value })} /></div>
                  <div><Label>Lng</Label><Input required className="mt-1" value={depot.lng} onChange={(e) => setDepot({ ...depot, lng: e.target.value })} /></div>
                </div>
                <div><Label>Roles (comma)</Label><Input className="mt-1" placeholder="train-driver, fitter" value={depot.roles} onChange={(e) => setDepot({ ...depot, roles: e.target.value })} /></div>
                <Button type="submit" className="w-full bg-navy text-bone hover:bg-lime-600 hover:text-ink">Add depot</Button>
              </form>
              <div className="bg-card border border-border rounded-lg overflow-x-auto">
                <div className="md:hidden divide-y divide-border">
                  {depots.length === 0 && (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">No depots added yet.</div>
                  )}
                  {depots.map((d) => (
                    <div key={`mobile-depot-${d.id}`} className="p-4 space-y-3" data-testid={`admin-depot-mobile-${d.id}`}>
                      <div>
                        <div className="font-semibold text-ink">{d.name || "—"}</div>
                        <div className="text-sm text-muted-foreground">{d.employer_name || "—"}</div>
                      </div>
                      <div className="text-xs">
                        <div className="uppercase tracking-wider text-muted-foreground">Postcode</div>
                        <div className="font-mono text-ink mt-0.5">{d.postcode || "—"}</div>
                      </div>
                      <div className="pt-1">
                        <Button size="sm" variant="outline" onClick={() => removeDepot(d.id)} data-testid={`admin-depot-delete-mobile-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <table className="hidden md:table w-full text-sm">
                  <thead className="bg-sand"><tr className="text-left">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Depot</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Employer</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Postcode</th>
                    <th className="px-4 py-3"></th>
                  </tr></thead>
                  <tbody>
                    {depots.map((d) => (
                      <tr key={d.id} className="border-t border-border">
                        <td className="px-4 py-3 font-semibold text-ink">{d.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{d.employer_name}</td>
                        <td className="px-4 py-3 font-mono text-xs">{d.postcode}</td>
                        <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => removeDepot(d.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="employers" className="mt-6 space-y-6">
            {/* Create trial account */}
            <div className="bg-card border border-border rounded-lg p-6" data-testid="create-trial-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-amber/20 inline-flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <div className="font-display font-bold text-ink text-xl tracking-tight">Create trial employer account</div>
                  <div className="text-sm text-muted-foreground">Set up a pre-approved account for a prospect. No email confirmation required — they can sign in straight away.</div>
                </div>
              </div>
              <form onSubmit={createTrialEmployer} className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Company name <span className="text-destructive">*</span></Label>
                  <Input className="mt-1" placeholder="e.g. Acme Rail Recruitment" value={newEmp.company_name} onChange={(ev) => setNewEmp({ ...newEmp, company_name: ev.target.value })} data-testid="trial-company" required />
                </div>
                <div>
                  <Label>Contact name</Label>
                  <Input className="mt-1" placeholder="e.g. Jane Doe (optional)" value={newEmp.contact_name} onChange={(ev) => setNewEmp({ ...newEmp, contact_name: ev.target.value })} data-testid="trial-contact" />
                </div>
                <div>
                  <Label>Email <span className="text-destructive">*</span></Label>
                  <Input type="email" className="mt-1" placeholder="contact@company.co.uk" value={newEmp.email} onChange={(ev) => setNewEmp({ ...newEmp, email: ev.target.value.toLowerCase() })} data-testid="trial-email" required />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input type="url" className="mt-1" placeholder="https://… (optional)" value={newEmp.website} onChange={(ev) => setNewEmp({ ...newEmp, website: ev.target.value })} data-testid="trial-website" />
                </div>
                <div className="md:col-span-2">
                  <Label>Password <span className="text-destructive">*</span></Label>
                  <div className="mt-1 flex gap-2">
                    <Input type="text" className="font-mono" placeholder="Min 8 characters" value={newEmp.password} onChange={(ev) => setNewEmp({ ...newEmp, password: ev.target.value })} data-testid="trial-password" minLength={8} required />
                    <Button type="button" variant="outline" onClick={generatePassword} data-testid="trial-generate-pw">Generate</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Shown in plain text deliberately so you can share it securely. The employer can change it once they log in.</p>
                </div>
                <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={newEmp.send_welcome_email} onChange={(ev) => setNewEmp({ ...newEmp, send_welcome_email: ev.target.checked })} data-testid="trial-send-email" />
                  <span>Email the employer their sign-in details (recommended)</span>
                </label>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={creatingEmp} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="trial-submit">
                    {creatingEmp ? "Creating…" : "Create trial account"}
                  </Button>
                </div>
              </form>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              <div className="md:hidden divide-y divide-border">
                {employers.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">No employer registrations yet.</div>
                )}
                {employers.map((e) => {
                  const isApproved = e.approved === true;
                  const verified = e.email_verified === true;
                  return (
                    <div key={`mobile-${e.id}`} className="p-4 space-y-3" data-testid={`admin-employer-mobile-${e.id}`}>
                      <div>
                        <div className="font-semibold text-ink flex items-center gap-2">
                          {e.company_name}
                          {e.is_trial && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber/30 text-ink font-bold" data-testid={`trial-badge-mobile-${e.id}`}>Trial</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{e.contact_name || "—"}</div>
                      </div>

                      <div className="text-sm">
                        <a className="hover:text-ink text-muted-foreground break-all" href={`mailto:${e.email}`}>{e.email}</a>
                        <div className="mt-1 text-xs">
                          {verified ? (
                            <span className="text-lime-700 font-semibold">Email verified</span>
                          ) : (
                            <span className="text-amber-700 font-semibold">Email unverified</span>
                          )}
                        </div>
                        {e.website && (
                          <div className="text-xs mt-1">
                            <a href={e.website} target="_blank" rel="noopener noreferrer nofollow" className="hover:text-ink underline break-all">{e.website}</a>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Joined</div>
                          <div className="text-ink font-mono mt-0.5">{(e.created_at || "").slice(0, 10)}</div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Status</div>
                          <div className="mt-0.5">
                            {isApproved ? (
                              <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-lime-100 text-lime-900">Approved</span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-amber/30 text-ink">Pending</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          size="sm"
                          variant={e.featured_employer ? "default" : "outline"}
                          onClick={() => toggleEmployerFeatured(e.id, e.featured_employer)}
                          className={e.featured_employer ? "bg-amber text-ink hover:bg-lime-600" : ""}
                          title={e.featured_employer ? "Remove featured flag" : "Feature this employer on homepage"}
                          data-testid={`admin-employer-feature-mobile-${e.id}`}
                        >
                          <Star className={"w-3.5 h-3.5 mr-1.5 " + (e.featured_employer ? "fill-ink" : "")} />
                          {e.featured_employer ? "Featured" : "Feature"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resetEmployerPassword(e.id, e.email)} data-testid={`admin-employer-reset-pw-mobile-${e.id}`}>Reset pw</Button>
                        <Button size="sm" variant="outline" onClick={() => pretendAs(e.id, "/employer-portal/dashboard")} data-testid={`admin-employer-pretend-mobile-${e.id}`}>Pretend</Button>
                        {isApproved ? (
                          <Button size="sm" variant="outline" onClick={() => setApproval(e.id, false)} data-testid={`admin-employer-revoke-mobile-${e.id}`}>Revoke</Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!verified}
                            title={verified ? "" : "Employer must confirm email first"}
                            className="bg-navy text-bone hover:bg-lime-600 hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setApproval(e.id, true)}
                            data-testid={`admin-employer-approve-mobile-${e.id}`}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <table className="hidden md:table w-full text-sm">
                <thead className="bg-sand"><tr className="text-left">
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Company</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Contact</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Email</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Joined</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Status</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold text-right">Action</th>
                </tr></thead>
                <tbody>
                  {employers.length === 0 && (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No employer registrations yet.</td></tr>
                  )}
                  {employers.map((e) => {
                    const isApproved = e.approved === true;
                    const verified = e.email_verified === true;
                    return (
                      <tr key={e.id} className="border-t border-border" data-testid={`admin-employer-${e.id}`}>
                        <td className="px-4 py-3 font-semibold text-ink">
                          {e.company_name}
                          {e.is_trial && (
                            <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber/30 text-ink font-bold" data-testid={`trial-badge-${e.id}`}>Trial</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{e.contact_name}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <a className="hover:text-ink" href={`mailto:${e.email}`}>{e.email}</a>
                            {verified ? (
                              <span title="Email confirmed" className="inline-flex items-center text-lime-600"><Mail className="w-3 h-3" /></span>
                            ) : (
                              <span title="Email NOT confirmed" className="text-amber-700 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber/30 font-semibold">unverified</span>
                            )}
                          </div>
                          {e.website && <div className="text-[11px] mt-0.5"><a href={e.website} target="_blank" rel="noopener noreferrer nofollow" className="hover:text-ink underline">{e.website}</a></div>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{(e.created_at || "").slice(0, 10)}</td>
                        <td className="px-4 py-3">
                          {isApproved ? (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-lime-100 text-lime-900">Approved</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-amber/30 text-ink">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right space-x-1.5">
                          <Button
                            size="sm"
                            variant={e.featured_employer ? "default" : "outline"}
                            onClick={() => toggleEmployerFeatured(e.id, e.featured_employer)}
                            className={e.featured_employer ? "bg-amber text-ink hover:bg-lime-600" : ""}
                            title={e.featured_employer ? "Remove featured flag" : "Feature this employer on homepage"}
                            data-testid={`admin-employer-feature-${e.id}`}
                          >
                            <Star className={"w-3.5 h-3.5 " + (e.featured_employer ? "fill-ink" : "")} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => resetEmployerPassword(e.id, e.email)} data-testid={`admin-employer-reset-pw-${e.id}`}>Reset pw</Button>
                          <Button size="sm" variant="outline" onClick={() => pretendAs(e.id, "/employer-portal/dashboard")} data-testid={`admin-employer-pretend-${e.id}`}>Pretend</Button>
                          {isApproved ? (
                            <Button size="sm" variant="outline" onClick={() => setApproval(e.id, false)} data-testid={`admin-employer-revoke-${e.id}`}>Revoke</Button>
                          ) : (
                            <Button
                              size="sm"
                              disabled={!verified}
                              title={verified ? "" : "Employer must confirm email first"}
                              className="bg-navy text-bone hover:bg-lime-600 hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => setApproval(e.id, true)}
                              data-testid={`admin-employer-approve-${e.id}`}
                            >
                              Approve
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="talentpool" className="mt-6">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm text-muted-foreground">
                  Need a test profile? Create a pretend candidate, then use Pretend to view the candidate dashboard.
                </div>
                <Button asChild variant="outline" data-testid="admin-open-candidate-explorer">
                  <Link to="/talent-pool/candidates">Open candidate explorer</Link>
                </Button>
              </div>
              <Button
                onClick={seedPretendCandidate}
                className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
                data-testid="admin-seed-pretend-candidate"
              >
                Create test candidate
              </Button>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              <div className="md:hidden divide-y divide-border">
                {talentMembers.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No talent-pool registrations yet.
                  </div>
                )}
                {talentMembers.map((m) => {
                  const draft = talentDrafts[m.id] || {
                    talent_pool_admin_status: "new",
                    talent_pool_stage: "interest_registered",
                    talent_pool_notes: "",
                  };
                  return (
                    <div key={`mobile-talent-${m.id}`} className="p-4 space-y-3" data-testid={`admin-talent-mobile-${m.id}`}>
                      <div>
                        <div className="font-semibold text-ink">{m.contact_name || "—"}</div>
                        <div className="text-xs text-muted-foreground mt-1">{m.email}</div>
                        <div className="text-[11px] mt-1">
                          {m.email_verified ? (
                            <span className="text-lime-700 font-semibold">Verified</span>
                          ) : (
                            <span className="text-amber-700 font-semibold">Unverified</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {(m.target_roles || []).length === 0 ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : (m.target_roles || []).map((role) => (
                          <span key={`mobile-${m.id}-${role}`} className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] text-ink">
                            {prettyTalentRole(role)}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Location</div>
                          <div className="text-ink mt-0.5">{m.preferred_locations || "—"}</div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Postcode</div>
                          <div className="text-ink font-mono mt-0.5">{m.preferred_postcode || "—"}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="uppercase tracking-wider text-muted-foreground">Stage One overall</div>
                          <div className="text-ink mt-0.5">{m.gateway_scores?.overall_stage_one_pct ?? "—"}%</div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Stage 2 outcome</div>
                          <div className="text-ink mt-0.5">{(m.stage_two_profile?.progression_outcome || "-").replaceAll("_", " ")}</div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider text-muted-foreground">Stage 2 safety</div>
                          <div className="text-ink mt-0.5">{m.stage_two_profile?.overall_safety_pct ?? "—"}%</div>
                        </div>
                        <div className="col-span-2">
                          <div className="uppercase tracking-wider text-muted-foreground">Stage 2 reason</div>
                          <div className="text-ink mt-0.5">{m.stage_two_profile?.outcome_reason_primary || "-"}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stageBadgeClass(draft.talent_pool_stage || m.talent_pool_stage)}`}>
                          {formatStageLabel(draft.talent_pool_stage || m.talent_pool_stage)}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${adminBadgeClass(draft.talent_pool_admin_status || m.talent_pool_admin_status)}`}>
                          Admin: {formatAdminStatusLabel(draft.talent_pool_admin_status || m.talent_pool_admin_status)}
                        </span>
                      </div>

                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</Label>
                        <select
                          className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                          value={draft.talent_pool_admin_status || "new"}
                          onChange={(e) => updateTalentDraft(m.id, "talent_pool_admin_status", e.target.value)}
                          data-testid={`admin-talent-status-mobile-${m.id}`}
                        >
                          {["new", "reviewing", "approved", "active", "shortlist_ready", "paused", "rejected"].map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Stage</Label>
                        <select
                          className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2 text-sm"
                          value={draft.talent_pool_stage || "interest_registered"}
                          onChange={(e) => updateTalentDraft(m.id, "talent_pool_stage", e.target.value)}
                          data-testid={`admin-talent-stage-mobile-${m.id}`}
                        >
                          {STAGE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Textarea
                          className="mt-1 min-h-[100px]"
                          value={draft.talent_pool_notes || ""}
                          onChange={(e) => updateTalentDraft(m.id, "talent_pool_notes", e.target.value)}
                          data-testid={`admin-talent-notes-mobile-${m.id}`}
                        />
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewingTalentMember(m)}
                          data-testid={`admin-talent-view-mobile-${m.id}`}
                        >
                          View responses
                        </Button>
                        <Button
                          size="sm"
                          className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
                          onClick={() => saveTalentMember(m.id)}
                          data-testid={`admin-talent-save-mobile-${m.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => messageTalentMember(m)}
                          data-testid={`admin-talent-message-mobile-${m.id}`}
                        >
                          Message
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => pretendAs(m.id, "/talent-pool/dashboard")}
                          data-testid={`admin-talent-pretend-mobile-${m.id}`}
                        >
                          Pretend
                        </Button>
                        <Button
                          size="sm"
                          className="bg-amber text-ink hover:bg-amber-600"
                          onClick={() => resetStageTwoForCandidate(m)}
                          data-testid={`admin-talent-reset-stage2-mobile-${m.id}`}
                        >
                          Reset Stage 2
                        </Button>
                        {m.stage_two_access_enabled ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleStageTwoAccess(m, false)}
                            data-testid={`admin-talent-stage2-disable-mobile-${m.id}`}
                          >
                            Disable Stage 2
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-lime-600 text-white hover:bg-lime-700"
                            onClick={() => toggleStageTwoAccess(m, true)}
                            data-testid={`admin-talent-stage2-enable-mobile-${m.id}`}
                          >
                            Open Stage 2 test
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <table className="hidden md:table w-full text-sm">
                <thead className="bg-sand">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Member</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Roles / location</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Postcode / score</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Pipeline</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Notes</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {talentMembers.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                        No talent-pool registrations yet.
                      </td>
                    </tr>
                  )}
                  {talentMembers.map((m) => {
                    const draft = talentDrafts[m.id] || {
                      talent_pool_admin_status: "new",
                      talent_pool_stage: "interest_registered",
                      talent_pool_notes: "",
                    };
                    return (
                      <tr key={m.id} className="border-t border-border" data-testid={`admin-talent-${m.id}`}>
                        <td className="px-4 py-3 align-top">
                          <div className="font-semibold text-ink">{m.contact_name || "—"}</div>
                          <div className="text-xs text-muted-foreground mt-1">{m.email}</div>
                          <div className="text-[11px] mt-1">
                            {m.email_verified ? (
                              <span className="text-lime-700 font-semibold">Verified</span>
                            ) : (
                              <span className="text-amber-700 font-semibold">Unverified</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-muted-foreground">
                          <div className="flex flex-wrap gap-1.5">
                            {(m.target_roles || []).length === 0 ? (
                              <span>—</span>
                            ) : (m.target_roles || []).map((role) => (
                              <span key={`${m.id}-${role}`} className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] text-ink">
                                {prettyTalentRole(role)}
                              </span>
                            ))}
                          </div>
                          <div className="text-xs mt-1">{m.preferred_locations || "—"}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-muted-foreground">
                          <div className="font-mono text-xs">{m.preferred_postcode || "—"}</div>
                          <div className="text-xs mt-1">Stage One overall: <span className="text-ink font-semibold">{m.gateway_scores?.overall_stage_one_pct ?? "—"}%</span></div>
                          <div className="text-xs mt-2">Stage 2 outcome: <span className="text-ink font-semibold">{(m.stage_two_profile?.progression_outcome || "-").replaceAll("_", " ")}</span></div>
                          <div className="text-xs">Stage 2 safety: {m.stage_two_profile?.overall_safety_pct ?? "—"}%</div>
                          <div className="text-xs mt-1">Stage 2 reason: {m.stage_two_profile?.outcome_reason_primary || "-"}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stageBadgeClass(draft.talent_pool_stage || m.talent_pool_stage)}`}>
                              {formatStageLabel(draft.talent_pool_stage || m.talent_pool_stage)}
                            </span>
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${adminBadgeClass(draft.talent_pool_admin_status || m.talent_pool_admin_status)}`}>
                              Admin: {formatAdminStatusLabel(draft.talent_pool_admin_status || m.talent_pool_admin_status)}
                            </span>
                          </div>
                          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</Label>
                          <select
                            className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                            value={draft.talent_pool_admin_status || "new"}
                            onChange={(e) => updateTalentDraft(m.id, "talent_pool_admin_status", e.target.value)}
                            data-testid={`admin-talent-status-${m.id}`}
                          >
                            {["new", "reviewing", "approved", "active", "shortlist_ready", "paused", "rejected"].map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                          <Label className="mt-3 block text-[11px] uppercase tracking-wider text-muted-foreground">Stage</Label>
                          <select
                            className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2 text-sm"
                            value={draft.talent_pool_stage || "interest_registered"}
                            onChange={(e) => updateTalentDraft(m.id, "talent_pool_stage", e.target.value)}
                            data-testid={`admin-talent-stage-${m.id}`}
                          >
                            {STAGE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <Textarea
                            className="min-h-[100px]"
                            value={draft.talent_pool_notes || ""}
                            onChange={(e) => updateTalentDraft(m.id, "talent_pool_notes", e.target.value)}
                            data-testid={`admin-talent-notes-${m.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 align-top text-right space-y-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingTalentMember(m)}
                            data-testid={`admin-talent-view-${m.id}`}
                          >
                            View responses
                          </Button>
                          <Button
                            size="sm"
                            className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
                            onClick={() => saveTalentMember(m.id)}
                            data-testid={`admin-talent-save-${m.id}`}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => messageTalentMember(m)}
                            data-testid={`admin-talent-message-${m.id}`}
                          >
                            Message
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pretendAs(m.id, "/talent-pool/dashboard")}
                            data-testid={`admin-talent-pretend-${m.id}`}
                          >
                            Pretend
                          </Button>
                          <Button
                            size="sm"
                            className="bg-amber text-ink hover:bg-amber-600"
                            onClick={() => resetStageTwoForCandidate(m)}
                            data-testid={`admin-talent-reset-stage2-${m.id}`}
                          >
                            Reset Stage 2
                          </Button>
                          {m.stage_two_access_enabled ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleStageTwoAccess(m, false)}
                              data-testid={`admin-talent-stage2-disable-${m.id}`}
                            >
                              Disable Stage 2
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-lime-600 text-white hover:bg-lime-700"
                              onClick={() => toggleStageTwoAccess(m, true)}
                              data-testid={`admin-talent-stage2-enable-${m.id}`}
                            >
                              Open Stage 2 test
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <CandidateResponsesPanel
            member={viewingTalentMember}
            isAdmin={true}
            open={!!viewingTalentMember}
            onClose={() => setViewingTalentMember(null)}
            onMessage={(m) => messageTalentMember(m)}
          />

          <TabsContent value="notifications" className="mt-6">
            <div className="grid lg:grid-cols-[420px_1fr] gap-6">
              <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="admin-inapp-compose">
                <div className="font-display font-bold text-ink text-xl tracking-tight">Send in-app notification</div>
                <div>
                  <Label>Subject</Label>
                  <Input className="mt-1" value={inappSubject} onChange={(e) => setInappSubject(e.target.value)} data-testid="inapp-subject" />
                </div>
                <div>
                  <Label>Message body</Label>
                  <Textarea className="mt-1 min-h-[140px]" value={inappBody} onChange={(e) => setInappBody(e.target.value)} data-testid="inapp-body" />
                </div>
                <div>
                  <Label>Format</Label>
                  <select className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2" value={inappFormat} onChange={(e) => setInappFormat(e.target.value)} data-testid="inapp-format">
                    <option value="text">Plain text</option>
                    <option value="html">HTML</option>
                  </select>
                </div>
                <div>
                  <Label>Recipient group</Label>
                  <select className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2" value={inappMode} onChange={(e) => setInappMode(e.target.value)} data-testid="inapp-mode">
                    <option value="individual">Individual</option>
                    <option value="employer_verified">Employer (verified)</option>
                    <option value="employer_unverified">Employer (unverified)</option>
                    <option value="talent_pool_all">Talent pool (all)</option>
                    <option value="talent_pool_stage">Talent pool by stage</option>
                  </select>
                </div>
                {inappMode === "individual" && (
                  <div>
                    <Label>Recipient email</Label>
                    <Input type="email" className="mt-1" value={inappRecipientEmail} onChange={(e) => setInappRecipientEmail(e.target.value)} data-testid="inapp-recipient-email" />
                  </div>
                )}
                {inappMode === "talent_pool_stage" && (
                  <div>
                    <Label>Talent pool stage</Label>
                    <select className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2" value={inappStage} onChange={(e) => setInappStage(e.target.value)} data-testid="inapp-stage">
                      <option value="">Select stage</option>
                      {availableTalentStages.map((stage) => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                  </div>
                )}
                <Button className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" onClick={sendInappNotification} disabled={inappSending} data-testid="inapp-send">
                  {inappSending ? "Sending..." : "Send notification"}
                </Button>
                <div className="text-xs text-muted-foreground">
                  In-app notifications are delivered to user dashboards. External and subscriber-only emails remain in the communications tools.
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="grid md:grid-cols-5 gap-2">
                    <Input
                      className="md:col-span-2"
                      value={inappSearch}
                      onChange={(e) => setInappSearch(e.target.value)}
                      placeholder="Search subject, recipient or snippet"
                      data-testid="inapp-search"
                    />
                    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground px-2">
                      <input
                        type="checkbox"
                        checked={inappUnreadOnly}
                        onChange={(e) => setInappUnreadOnly(e.target.checked)}
                        data-testid="inapp-filter-unread"
                      />
                      Unread only
                    </label>
                    <Button variant="outline" onClick={() => bulkArchiveInapp(true)} data-testid="inapp-bulk-archive">Archive selected</Button>
                    <Button
                      variant="outline"
                      onClick={() => setInappSelectedIds(filteredInappThreads.map((t) => t.id))}
                      data-testid="inapp-select-visible"
                    >
                      Select visible
                    </Button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{inappSelectedIds.length} selected</span>
                    <button type="button" className="underline" onClick={() => setInappSelectedIds([])} data-testid="inapp-clear-selection">Clear selection</button>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="admin-inapp-inbox-list">
                  <div className="px-4 py-3 border-b border-border bg-sand text-xs uppercase tracking-wider text-muted-foreground">Inbox</div>
                  <div className="max-h-[280px] overflow-auto">
                    {filteredInappThreads.length === 0 ? (
                      <div className="p-6 text-sm text-muted-foreground">No inbox threads.</div>
                    ) : filteredInappThreads.map((t) => (
                      <div key={t.id} className="px-4 py-3 border-b border-border hover:bg-paper/40" data-testid={`inapp-thread-${t.id}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={inappSelectedIds.includes(t.id)}
                            onChange={() => toggleInappSelected(t.id)}
                          />
                          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => loadInappThread(t.id)}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold text-ink truncate">{t.subject}</div>
                              {!t.admin_read_at && <span className="text-[10px] px-2 py-0.5 rounded bg-amber/30 text-ink font-semibold">Unread</span>}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{t.recipient_label || t.recipient_email}</div>
                            <div className="text-xs text-muted-foreground mt-1 truncate">{t.last_message_preview || "—"}</div>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4" data-testid="admin-inapp-thread-view">
                  {!inappActiveThread ? (
                    <div className="text-sm text-muted-foreground">Select a thread to view messages.</div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-display font-bold text-ink text-lg">{inappActiveThread.subject}</div>
                          <div className="text-xs text-muted-foreground mt-1">To: {inappActiveThread.recipient_label || inappActiveThread.recipient_email}</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => archiveInappThread(inappActiveThread.id, true)} data-testid="inapp-thread-archive">Archive</Button>
                      </div>

                      <div className="mt-4 max-h-[300px] overflow-auto border border-border rounded-lg bg-background p-3 space-y-3">
                        {inappMessages.map((m) => (
                          <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.sender_role === "admin" ? "bg-sky/10 border border-sky/30" : "bg-paper border border-border"}`}>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.sender_role === "admin" ? "Admin" : "User"}</div>
                            <div className="mt-1 whitespace-pre-wrap">{m.body_text || ""}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4">
                        <Label>Reply</Label>
                        <Textarea className="mt-1 min-h-[90px]" value={inappReplyBody} onChange={(e) => setInappReplyBody(e.target.value)} data-testid="inapp-reply-body" />
                        <div className="mt-2 flex gap-2">
                          <Button onClick={replyInappThread} disabled={inappReplySending} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink" data-testid="inapp-reply-send">
                            {inappReplySending ? "Sending..." : "Send reply"}
                          </Button>
                          <Button variant="outline" onClick={() => loadInappThread(inappActiveThread.id)} data-testid="inapp-thread-refresh">Refresh</Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notifications-archive" className="mt-6">
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="admin-inapp-archive-list">
              <div className="px-4 py-3 border-b border-border bg-sand text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Archived threads</span>
                <Button size="sm" variant="outline" onClick={() => bulkArchiveInapp(false)} data-testid="inapp-bulk-unarchive">Restore selected</Button>
              </div>
              <div className="px-4 py-3 border-b border-border bg-paper/30">
                <Input
                  value={inappSearch}
                  onChange={(e) => setInappSearch(e.target.value)}
                  placeholder="Search archived threads"
                  data-testid="inapp-archive-search"
                />
              </div>
              <div className="max-h-[640px] overflow-auto">
                {filteredInappArchivedThreads.length === 0 ? (
                  <div className="p-8 text-sm text-muted-foreground">No archived notifications.</div>
                ) : filteredInappArchivedThreads.map((t) => (
                  <div key={t.id} className="px-4 py-3 border-b border-border">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={inappSelectedIds.includes(t.id)}
                            onChange={() => toggleInappSelected(t.id)}
                          />
                          Select
                        </label>
                        <div className="font-semibold text-ink">{t.subject}</div>
                        <div className="text-xs text-muted-foreground mt-1">{t.recipient_label || t.recipient_email}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => archiveInappThread(t.id, false)} data-testid={`inapp-unarchive-${t.id}`}>Move to inbox</Button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2 truncate">{t.last_message_preview || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="subscribers" className="mt-6">
            <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="admin-subscriber-notify-card">
              <div className="font-display font-bold text-ink text-xl tracking-tight">Newsletter and communications</div>
              <p className="text-sm text-muted-foreground mt-1">
                Send text or HTML updates with a preview. Messages are automatically wrapped in Railway Careers UK email styling before send.
              </p>

              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Subject</Label>
                  <Input
                    className="mt-1"
                    value={subMsgSubject}
                    onChange={(e) => setSubMsgSubject(e.target.value)}
                    data-testid="submsg-subject"
                  />
                </div>
                <div>
                  <Label>Body format</Label>
                  <select
                    className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                    value={subMsgFormat}
                    onChange={(e) => setSubMsgFormat(e.target.value)}
                    data-testid="submsg-format"
                  >
                    <option value="text">Plain text</option>
                    <option value="html">HTML</option>
                  </select>
                </div>
                <div>
                  <Label>Recipient group</Label>
                  <select
                    className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                    value={subMsgRecipientMode}
                    onChange={(e) => setSubMsgRecipientMode(e.target.value)}
                    data-testid="submsg-recipient-mode"
                  >
                    <option value="individual">Individual</option>
                    <option value="employer_verified">Employer (verified)</option>
                    <option value="employer_unverified">Employer (unverified)</option>
                    <option value="talent_pool_all">Talent pool (all)</option>
                    <option value="talent_pool_stage">Talent pool by stage</option>
                    <option value="subscribers_all">Subscribers (all)</option>
                    <option value="external_single">External email single</option>
                    <option value="external_group_bcc">External email group (BCC)</option>
                  </select>
                </div>

                {(subMsgRecipientMode === "individual" || subMsgRecipientMode === "external_single") && (
                  <div>
                    <Label>Recipient email</Label>
                    <Input
                      type="email"
                      className="mt-1"
                      value={subMsgRecipientEmail}
                      onChange={(e) => setSubMsgRecipientEmail(e.target.value)}
                      placeholder="person@example.com"
                      data-testid="submsg-recipient-email"
                    />
                  </div>
                )}

                {subMsgRecipientMode === "talent_pool_stage" && (
                  <div>
                    <Label>Talent-pool stage</Label>
                    <select
                      className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                      value={subMsgTalentStage}
                      onChange={(e) => setSubMsgTalentStage(e.target.value)}
                      data-testid="submsg-talent-stage"
                    >
                      <option value="">Select stage</option>
                      {availableTalentStages.map((stage) => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                  </div>
                )}

                {subMsgRecipientMode === "external_group_bcc" && (
                  <div className="md:col-span-2">
                    <Label>External emails (comma or new-line separated)</Label>
                    <Textarea
                      className="mt-1 min-h-[90px] font-mono text-xs"
                      value={subMsgExternalEmails}
                      onChange={(e) => setSubMsgExternalEmails(e.target.value)}
                      placeholder={"a@example.com\nb@example.com\nc@example.com"}
                      data-testid="submsg-external-group"
                    />
                    <div className="mt-1 text-xs text-muted-foreground">Group external sends are delivered using BCC so recipients cannot see each other.</div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <Label>Message body</Label>
                  <Textarea
                    className="mt-1 min-h-[150px] font-mono text-sm"
                    value={subMsgBody}
                    onChange={(e) => setSubMsgBody(e.target.value)}
                    placeholder={subMsgFormat === "html" ? "<h2>Service update</h2><p>...</p>" : "Service update\n\nHello subscribers,..."}
                    data-testid="submsg-body"
                  />
                </div>
                <div>
                  <Label>Test email (optional)</Label>
                  <Input
                    type="email"
                    className="mt-1"
                    value={subMsgTestEmail}
                    onChange={(e) => setSubMsgTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="submsg-test-email"
                  />
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={previewSubscriberNotification}
                    disabled={subMsgPreviewing || subMsgSending}
                    data-testid="submsg-preview"
                  >
                    {subMsgPreviewing ? "Previewing…" : "Preview"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => sendSubscriberNotification({ useTestEmail: true })}
                    disabled={subMsgSending}
                    data-testid="submsg-send-test"
                  >
                    Send test
                  </Button>
                  <Button
                    type="button"
                    className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold"
                    onClick={() => sendSubscriberNotification()}
                    disabled={subMsgSending}
                    data-testid="submsg-send-all"
                  >
                    {subMsgSending ? "Sending…" : "Send now"}
                  </Button>
                </div>
              </div>

              <div className="mt-6 border border-border rounded-lg overflow-hidden" data-testid="submsg-preview-pane">
                <div className="px-4 py-3 border-b border-border bg-sand text-xs text-muted-foreground flex flex-wrap gap-3">
                  <span>Preview</span>
                  {subMsgPreview && <span className="font-mono">Recipients: {subMsgPreview.recipient_count}</span>}
                  {subMsgPreview?.uses_bcc && <span className="font-mono">Delivery: BCC group</span>}
                  {subMsgPreview?.resolved_mode && <span className="font-mono">Mode: {subMsgPreview.resolved_mode}</span>}
                </div>
                {subMsgPreview ? (
                  <div className="max-h-[480px] overflow-auto p-4 bg-background">
                    <div dangerouslySetInnerHTML={{ __html: subMsgPreview.html }} />
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Build a preview before sending.
                  </div>
                )}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sand"><tr className="text-left">
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Email</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Name</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">{s.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{(s.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendSubscriberNotification({ recipientEmail: s.email })}
                          disabled={subMsgSending}
                          data-testid={`submsg-send-one-${s.id}`}
                        >
                          Message
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="space-y-3">
              {contacts.length === 0 && <div className="text-muted-foreground p-8 text-center bg-card border border-border rounded-lg">No sales requests yet.</div>}
              {contacts.map((c) => (
                <div key={c.id} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-display font-bold text-ink">{c.company}</div>
                      <div className="text-sm text-muted-foreground">{c.contact_name} · <a href={`mailto:${c.email}`} className="text-ink">{c.email}</a> {c.phone ? `· ${c.phone}` : ""}</div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{(c.created_at || "").slice(0, 16)}</div>
                  </div>
                  <p className="text-sm mt-3 whitespace-pre-line">{c.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
